import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Modal from "@/components/Modal";
import AgenticMetricsPanel from "@/components/AgenticMetricsPanel";
import { notify, fmtError } from "@/lib/notify";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Crown, Plus, X, Loader2, AlertTriangle, Building2, Users as UsersIcon,
  Trash2, Edit2, Check, UserCheck, Sparkles, KeyRound, Lock, Unlock, Layers,
  Activity as ActivityIcon, TrendingUp, Mail, Inbox, ShieldCheck, ShieldX,
  RefreshCw, ExternalLink, Filter, ArrowLeft, Database, Pencil, UserPlus, Eraser, Flame, Brain,
} from "lucide-react";
import ThreeQCoverageBadge from "@/components/ThreeQCoverageBadge";
import EnvSyncPanel from "@/components/ultra/EnvSyncPanel";
import ByokConfigPanel from "@/components/ultra/ByokConfigPanel";
import DvumPanel from "@/components/ultra/DvumPanel";

const PLAN_LABELS = {
  base: { label: "Base", color: "#A1A1AA" },
  pro: { label: "Pro", color: "#378ADD" },
  enterprise: { label: "Enterprise", color: "#7F77DD" },
  test: { label: "Test (lancement)", color: "#EF9F27" },
  personnalise: { label: "Personnalisé", color: "#1D9E75" },
};

function fmtErr(d) {
  if (!d) return "Erreur.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(d);
}

// V23.16.3 — Mini-badge "live" affiché à côté de "Environnement global" dans le header.
// Poll /ultra-admin/online-users (fenêtre 15min) toutes les 30s pour afficher le
// nombre d'utilisateurs en ligne en temps réel. Cliquable → ouvre l'onglet Activité.
function LiveBadge({ onClick }) {
  const [count, setCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await api.get("/ultra-admin/online-users?window_min=15");
        if (!cancelled) setCount(r.data?.counts?.online ?? 0);
      } catch {
        if (!cancelled) setCount(null);
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (count === null) return null;
  const isOnline = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border transition ${
        isOnline
          ? "bg-[#1D9E75]/15 border-[#1D9E75]/40 text-[#1D9E75] hover:bg-[#1D9E75]/25"
          : "bg-white/[0.04] border-white/10 text-zinc-500 hover:text-zinc-300"
      }`}
      title={isOnline ? `${count} utilisateur(s) en ligne — cliquer pour voir le détail` : "Personne de connecté actuellement"}
      data-testid="ultra-admin-live-badge"
    >
      <span className="relative inline-flex w-1.5 h-1.5">
        {isOnline && (
          <span className="absolute inset-0 rounded-full bg-[#1D9E75] opacity-75 animate-ping" />
        )}
        <span
          className="relative rounded-full w-1.5 h-1.5"
          style={{ background: isOnline ? "#1D9E75" : "#52525B" }}
        />
      </span>
      <span data-testid="ultra-admin-live-count">{count}</span>
      <span className="uppercase tracking-wider">live</span>
    </button>
  );
}

export default function UltraAdmin() {
  const { user: me } = useAuth();
  const { t } = useTranslation();
  const [tab, setTab] = useState("orgs");

  // Auth context starts at `null` (loading) — show a spinner instead of
  // flashing the access-denied card before /auth/me resolves.
  if (me === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--noam-bg)] text-zinc-400 text-sm" data-testid="ultra-admin-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  if (!me?.is_super_admin && !me?.is_ultra_admin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--noam-bg)] text-white p-6" data-testid="ultra-admin-denied">
        <div className="noam-card p-8 max-w-md text-center">
          <Crown className="w-10 h-10 text-[#EF9F27] mx-auto opacity-70" />
          <div className="noam-display text-xl font-semibold mt-4">Accès Ultra Admin</div>
          <p className="text-zinc-400 text-sm mt-2">Cette zone est réservée à l'ultra admin de la plateforme.</p>
          <div className="text-[10px] text-zinc-600 mt-4 text-left bg-black/30 rounded p-3 font-mono">
            <div>email: {me?.email || "—"}</div>
            <div>is_super_admin: {String(!!me?.is_super_admin)}</div>
            <div>is_ultra_admin: {String(!!me?.is_ultra_admin)}</div>
            <div>role: {me?.role || "—"}</div>
          </div>
          <a href="/login" className="text-xs text-[#378ADD] hover:underline mt-4 inline-block" data-testid="ultra-admin-relogin">
            Se reconnecter
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--noam-bg)] text-white" data-testid="ultra-admin-root">
      <header className="border-b border-white/5 px-6 py-4 flex items-center justify-between bg-[#EF9F27]/5">
        <div className="flex items-center gap-3">
          <Link
            to="/app/executive"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-zinc-100 transition-colors px-2 py-1 rounded border border-white/10 hover:border-white/20"
            data-testid="ultra-admin-back-to-cockpit"
            title={t("nav.back_to_cockpit", { defaultValue: "Retour à l'environnement" })}
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t("nav.back_to_cockpit", { defaultValue: "Retour à l'environnement" })}</span>
          </Link>
          <Crown className="w-5 h-5 text-[#EF9F27]" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#EF9F27] font-semibold">Ultra Admin · Plateforme Noam</div>
            <div className="noam-display text-lg font-semibold inline-flex items-center gap-2">
              Environnement global
              <LiveBadge onClick={() => setTab("activity")} />
            </div>
          </div>
        </div>
        <div className="text-xs text-zinc-400">{me?.email}</div>
      </header>
      <div className="px-6 py-6 space-y-6">
        <div className="flex gap-1 border-b border-white/5">
          <TabBtn active={tab === "orgs"} onClick={() => setTab("orgs")} icon={Building2} label="Organisations & Plans" testid="ua-tab-orgs" />
          <TabBtn active={tab === "dvum"} onClick={() => setTab("dvum")} icon={TrendingUp} label="DVUM" testid="ua-tab-dvum" />
          <TabBtn active={tab === "plans"} onClick={() => setTab("plans")} icon={Layers} label="Plans" testid="ua-tab-plans" />
          <TabBtn active={tab === "trend"} onClick={() => setTab("trend")} icon={ActivityIcon} label="Trend Intelligence" testid="ua-tab-trend" />
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={UsersIcon} label="Utilisateurs (toutes orgs)" testid="ua-tab-users" />
          <TabBtn active={tab === "pilots"} onClick={() => setTab("pilots")} icon={UserCheck} label="Suivi pilotes" testid="ua-tab-pilots" />
          <TabBtn active={tab === "envsync"} onClick={() => setTab("envsync")} icon={RefreshCw} label="Sync prod" testid="ua-tab-envsync" />
          <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} icon={Inbox} label="Demandes d'accès" testid="ua-tab-requests" />
          <TabBtn active={tab === "activity"} onClick={() => setTab("activity")} icon={ActivityIcon} label="Activité" testid="ua-tab-activity" />
          <TabBtn active={tab === "llm"} onClick={() => setTab("llm")} icon={TrendingUp} label="Quotas Claude" testid="ua-tab-llm" />
          <TabBtn active={tab === "obs"} onClick={() => setTab("obs")} icon={ActivityIcon} label="Observabilité" testid="ua-tab-obs" />
          <TabBtn active={tab === "datalayer"} onClick={() => setTab("datalayer")} icon={Database} label="Data Layer" testid="ua-tab-datalayer" />
          <TabBtn active={tab === "doctrine3q"} onClick={() => setTab("doctrine3q")} icon={ShieldCheck} label="Doctrine 3Q" testid="ua-tab-doctrine3q" />
          <TabBtn active={tab === "emails_hebdo"} onClick={() => setTab("emails_hebdo")} icon={Mail} label="Emails hebdo" testid="ua-tab-emails-hebdo" />
          <Link
            to="/app/metacognition"
            className="px-3 py-2 text-xs flex items-center gap-1.5 text-[#8B7BE8] hover:text-white border-b-2 border-transparent hover:border-[#8B7BE8]/50 transition-colors"
            data-testid="ua-link-metacognition"
            title="Métacognition — gouvernance plateforme (Ultra-Admin only)"
          >
            <Brain className="w-3.5 h-3.5" /> Métacognition
          </Link>
          <a
            href="/politique-confidentialite"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium border border-white/10 hover:border-white/30 text-zinc-400 hover:text-white transition-colors"
            data-testid="ua-tab-politique-link"
            title="Voir la politique de confidentialité publique (nouvel onglet)"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Politique · Confidentialité
            <ExternalLink className="w-3 h-3 opacity-60" />
          </a>
        </div>
        {tab === "orgs" && (
          <>
            <div className="mb-6"><AgenticMetricsPanel /></div>
            <OrgsPanel />
          </>
        )}
        {tab === "dvum" && <DvumPanel />}
        {tab === "plans" && <PlansPanel />}
        {tab === "trend" && <UltraTrendPanel />}
        {tab === "users" && <UsersPanel />}
        {tab === "pilots" && <PilotsPanel />}
        {tab === "envsync" && <EnvSyncPanel />}
        {tab === "requests" && <AccessRequestsPanel />}
        {tab === "activity" && <ActivityPanel />}
        {tab === "llm" && (<><LlmQuotaPanel /><ByokConfigPanel /></>)}
        {tab === "obs" && <ObservabilityPanel />}
        {tab === "datalayer" && <DataLayerPanel />}
        {tab === "doctrine3q" && <Doctrine3QPanel />}
        {tab === "emails_hebdo" && <EmailsHebdoPanel />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition-colors ${
        active ? "border-[#EF9F27] text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"
      }`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function OrgsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showModules, setShowModules] = useState(null); // org id when opening module editor
  const [editingMetaOrg, setEditingMetaOrg] = useState(null); // V23.15.3 — rename modal
  const [tempCred, setTempCred] = useState(null);

  const load = async () => {
    try { const r = await api.get("/ultra-admin/organisations"); setData(r.data); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const setPlan = async (orgId, plan) => {
    try { await api.patch(`/ultra-admin/organisations/${orgId}/plan`, { plan }); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  const remove = async (org) => {
    if (!window.confirm(`Supprimer l'organisation "${org.name}" et toutes ses données ?`)) return;
    try { await api.delete(`/workspace/admin/organisations/${org.id}`); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  const impersonate = async (org) => {
    if (!window.confirm(`Entrer dans le workspace "${org.name}" ?`)) return;
    try { await api.post(`/workspace/admin/organisations/${org.id}/impersonate`); window.location.href = "/app/executive"; }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  const seedDemo = async (org) => {
    if (!org.is_demo_workspace) {
      notify.info("Active d'abord le flag « Workspace de démo » dans l'édition de l'organisation.");
      return;
    }
    const typed = window.prompt(
      `⚠️ Insertion de DONNÉES FICTIVES (banque privée suisse — Portefeuille Lombard, Avaloq, RSK-CYB-001…)\n\n` +
      `Cible : "${org.name}"\n\n` +
      `Pour confirmer, tape exactement le slug : ${org.slug}`
    );
    if (typed !== org.slug) {
      if (typed !== null) notify.info("Seed annulé (slug incorrect).");
      return;
    }
    try {
      await api.post(`/workspace/admin/organisations/${org.id}/impersonate`);
      const r = await api.post("/workspace/admin/seed-demo-data");
      const ins = r.data?.inserted || {};
      const desc = Object.keys(ins).length === 0
        ? "Aucune insertion (déjà rempli)."
        : Object.entries(ins).map(([k, v]) => `${k}: ${v}`).join(" · ");
      notify.success(`Données insérées dans « ${r.data.organisation_name} »`, { description: desc });
      await api.post("/workspace/admin/stop-impersonation");
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  // V23.16.4/5 — Purge ciblée (mode="seed") OU totale (mode="all") des données.
  // - "seed" : signature whitelist (Portefeuille Lombard, Avaloq, RSK-CYB-001…)
  // - "all"  : nuke complet des 10 collections pour l'org (triple-confirm)
  // Dans les deux cas : invalidation caches IA + reset org sector/desc/size.
  const purgeDemo = async (org, mode = "seed") => {
    try {
      // 1. Dry-run pour montrer ce qui sera supprimé
      const dry = await api.post("/workspace/admin/purge-demo-seed", {
        organisation_id: org.id, dry_run: true, mode,
      });
      const d = dry.data || {};
      const lines = [];
      let totalLegacy = 0;
      let totalTagged = 0;
      for (const [coll, v] of Object.entries(d.details || {})) {
        if ((v.tagged_matches || 0) + (v.legacy_matches || 0) > 0) {
          lines.push(`  • ${coll}: ${v.tagged_matches + v.legacy_matches} doc(s)`);
          totalLegacy += v.legacy_matches || 0;
          totalTagged += v.tagged_matches || 0;
        }
      }
      const total = totalLegacy + totalTagged;
      if (total === 0) {
        notify.success("Aucune donnée à purger — l'organisation est déjà propre.");
        return;
      }
      const modeLabel = mode === "all" ? "PURGE TOTALE" : "purge ciblée";
      const intro = mode === "all"
        ? `⚠️⚠️ PURGE TOTALE pour "${org.name}"\n\n` +
          `Cela va EFFACER ${total} document(s) dans 10 collections (risques, audits, actifs, fournisseurs, contrôles, incidents, alertes, plans, opportunités, postes RH), ` +
          `puis invalider tous les caches IA. Cela inclut TOUTES les données saisies, pas seulement le seed.\n\n`
        : `⚠️ Purger les données de démonstration pour "${org.name}" ?\n\n` +
          `${total} document(s) seedés (Portefeuille Lombard, Avaloq, codes RSK-XXX, etc.) seront supprimés ` +
          `et tous les caches IA invalidés pour régénération propre.\n\n`;
      const ok = window.confirm(
        intro + `Détail :\n${lines.join("\n")}\n\n` +
        `Cette action est IRRÉVERSIBLE. Continuer ?`
      );
      if (!ok) return;
      // Triple-confirm pour mode "all"
      if (mode === "all") {
        const typed = window.prompt(
          `Pour confirmer la PURGE TOTALE, tape exactement le slug de l'organisation :\n\n${org.slug}`
        );
        if (typed !== org.slug) {
          notify.info("Purge annulée (slug incorrect).");
          return;
        }
      }
      // 2. Execute
      const r = await api.post("/workspace/admin/purge-demo-seed", {
        organisation_id: org.id, confirm_slug: org.slug, mode,
      });
      const dd = r.data || {};
      const aiCount = Object.values(dd.ai_caches_purged || {}).reduce((s, n) => s + n, 0);
      notify.success(`${modeLabel.toUpperCase()} terminée pour « ${dd.organisation_name} »`, {
        description: `${dd.total_deleted} doc(s) supprimés · ${aiCount} cache(s) IA invalidé(s)` +
          (dd.org_sector_reset ? " · sector reset" : "") +
          (dd.org_description_reset ? " · description reset" : ""),
      });
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  const orgInModuleEditor = data?.organisations.find((o) => o.id === showModules);

  return (
    <>
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs" data-testid="ua-org-create-btn">
          <Plus className="w-3.5 h-3.5" /> Créer une organisation
        </button>
      </div>
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="noam-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
          <div className="col-span-3">Organisation</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2 text-center">Modules actifs</div>
          <div className="col-span-1 text-center">Utilisateurs</div>
          <div className="col-span-4 text-right">Actions</div>
        </div>
        {!data && <div className="px-5 py-6 text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}
        {data?.organisations?.map((o) => {
          const pm = PLAN_LABELS[o.plan] || PLAN_LABELS.test;
          return (
            <div key={o.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02]" data-testid={`ua-org-row-${o.slug}`}>
              <div className="col-span-3">
                <div className="text-white truncate">{o.name}</div>
                <div className="text-[10px] text-zinc-600 mt-0.5">{o.slug} · {o.sector || "—"}</div>
              </div>
              <div className="col-span-2">
                <select value={o.plan} onChange={(e) => setPlan(o.id, e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1 text-xs text-white focus:border-[#378ADD] focus:outline-none"
                  data-testid={`ua-org-plan-${o.slug}`}
                  style={{ borderColor: pm.color + "55" }}>
                  {(data.plans_meta && data.plans_meta.length > 0
                    ? data.plans_meta
                    : (data.plans || []).map((p) => ({ id: p, name: (PLAN_LABELS[p] || PLAN_LABELS.test).label }))
                  ).map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2 text-center text-xs text-zinc-300">
                <span className="text-[#378ADD] font-semibold">{o.active_modules.length}</span> / {Object.keys(data.available_modules).length}
                {o.modules_locked.length > 0 && (
                  <span className="ml-1 text-[10px] text-[#EF9F27]"><Lock className="w-2.5 h-2.5 inline" /> {o.modules_locked.length}</span>
                )}
              </div>
              <div className="col-span-1 text-center text-xs text-zinc-400">{o.user_count}</div>
              <div className="col-span-4 flex items-center justify-end gap-1">
                <button onClick={() => setEditingMetaOrg(o)} className="text-zinc-400 hover:text-[#378ADD] p-1.5 rounded hover:bg-white/5" title="Renommer / modifier les métadonnées" data-testid={`ua-org-rename-${o.slug}`}>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setShowModules(o.id)} className="text-zinc-400 hover:text-[#378ADD] p-1.5 rounded hover:bg-white/5" title="Modules" data-testid={`ua-org-modules-${o.slug}`}>
                  <Layers className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => seedDemo(o)}
                  disabled={!o.is_demo_workspace}
                  className={`p-1.5 rounded ${o.is_demo_workspace ? "text-[#EF9F27] hover:text-[#EF9F27] hover:bg-[#EF9F27]/10 border border-[#EF9F27]/30" : "text-zinc-700 cursor-not-allowed opacity-40"}`}
                  title={o.is_demo_workspace ? "⚠️ Peupler données fictives (DEMO ONLY)" : "Désactivé : ce workspace n'est pas marqué « démo » (édite l'org pour activer le flag)"}
                  data-testid={`ua-org-seed-${o.slug}`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => purgeDemo(o, "seed")} className="text-zinc-400 hover:text-[#EF9F27] p-1.5 rounded hover:bg-white/5" title="Purger les données de démonstration (signature ciblée)" data-testid={`ua-org-purge-demo-${o.slug}`}>
                  <Eraser className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => purgeDemo(o, "all")} className="text-zinc-400 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="PURGE TOTALE — efface toutes les données métier (10 collections) + caches IA. Triple-confirm." data-testid={`ua-org-purge-all-${o.slug}`}>
                  <Flame className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => impersonate(o)} className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/25" title="Entrer dans l'organisation (impersonation)" data-testid={`ua-org-impersonate-${o.slug}`}>
                  <UserCheck className="w-3 h-3" /> Entrer
                </button>
                <button onClick={() => remove(o)} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="Supprimer" data-testid={`ua-org-delete-${o.slug}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {showCreate && (
        <CreateOrgUltraModal
          plans={data?.plans_meta && data.plans_meta.length > 0 ? data.plans_meta : (data?.plans || []).map((p) => ({ id: p, name: (PLAN_LABELS[p] || PLAN_LABELS.test).label }))}
          onClose={() => setShowCreate(false)}
          onCreated={(cred) => {
            setShowCreate(false);
            if (cred?.temp_password) setTempCred({ email: cred.admin_email, password: cred.temp_password });
            load();
          }}
        />
      )}
      {orgInModuleEditor && (
        <ModuleEditorModal
          org={orgInModuleEditor}
          available={data.available_modules}
          planModules={data.plan_modules[orgInModuleEditor.plan] || []}
          onClose={() => setShowModules(null)}
          onSaved={() => { setShowModules(null); load(); }}
        />
      )}
      {editingMetaOrg && (
        <EditOrgMetaModal
          org={editingMetaOrg}
          onClose={() => setEditingMetaOrg(null)}
          onSaved={() => { setEditingMetaOrg(null); load(); }}
        />
      )}
      {tempCred && <TempCredView cred={tempCred} onClose={() => setTempCred(null)} />}
    </>
  );
}

// V23.15.3 — Modal de renommage / édition métadonnées de l'organisation
function EditOrgMetaModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: org.name || "",
    slug: org.slug || "",
    sector: org.sector || "",
    phase: org.phase || "Stabilisation",
    status: org.status || "active",
    is_demo_workspace: !!org.is_demo_workspace,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const diff = {};
      ["name", "slug", "sector", "phase", "status"].forEach((k) => {
        if (form[k] !== (org[k] || "")) diff[k] = form[k];
      });
      if (form.is_demo_workspace !== !!org.is_demo_workspace) {
        diff.is_demo_workspace = form.is_demo_workspace;
      }
      if (Object.keys(diff).length === 0) { onClose(); return; }
      await api.patch(`/ultra-admin/organisations/${org.id}`, diff);
      notify.success("Organisation mise à jour");
      onSaved?.();
    } catch (e2) {
      setError(fmtErr(e2?.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ua-org-rename-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="noam-card max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Modifier l&apos;organisation</h3>
          <button type="button" onClick={onClose} className="text-zinc-400"><X className="w-4 h-4" /></button>
        </div>
        {error && <div className="text-xs text-[#FCA5A5] border border-[#E24B4A]/30 bg-[#E24B4A]/10 p-2 rounded">{error}</div>}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom *</label>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="noam-input w-full" required data-testid="ua-org-edit-name" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Slug</label>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="noam-input w-full" data-testid="ua-org-edit-slug" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Secteur</label>
            <input value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} className="noam-input w-full" data-testid="ua-org-edit-sector" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Phase</label>
            <select value={form.phase} onChange={(e) => setForm({ ...form, phase: e.target.value })} className="noam-input w-full" data-testid="ua-org-edit-phase">
              <option value="Stabilisation">Stabilisation</option>
              <option value="Déploiement">Déploiement</option>
              <option value="Restructuration">Restructuration</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Statut</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="noam-input w-full" data-testid="ua-org-edit-status">
              <option value="active">active</option>
              <option value="suspended">suspended</option>
              <option value="archived">archived</option>
            </select>
          </div>
        </div>
        {/* V23.16.6 — Flag is_demo_workspace : autorise le seed-demo-data sur cette org */}
        <div className={`rounded p-3 border ${form.is_demo_workspace ? "bg-[#EF9F27]/8 border-[#EF9F27]/40" : "bg-white/[0.02] border-white/10"}`}>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_demo_workspace}
              onChange={(e) => setForm({ ...form, is_demo_workspace: e.target.checked })}
              className="mt-0.5 accent-[#EF9F27]"
              data-testid="ua-org-edit-is-demo-workspace"
            />
            <div className="flex-1">
              <div className="text-xs font-medium text-zinc-100 inline-flex items-center gap-1.5">
                <Sparkles className="w-3 h-3 text-[#EF9F27]" /> Workspace de démo (autorise le seed de données fictives)
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                Si activé : permet d&apos;utiliser le bouton « Peupler données démo » sur cette organisation. Si désactivé (recommandé pour les workspaces clients) : le seed est bloqué — protège contre les pollutions accidentelles (Portefeuille Lombard, Avaloq, RSK-CYB-001…).
              </div>
            </div>
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary text-xs">Annuler</button>
          <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="ua-org-edit-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Enregistrer
          </button>
        </div>
      </form>
    </div>
  );
}

function CreateOrgUltraModal({ plans, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", sector: "", admin_email: "", admin_password: "", plan: "test" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post("/workspace/admin/organisations/with-admin", {
        name: form.name.trim(),
        sector: form.sector || null,
        plan: form.plan,
        admin_email: form.admin_email.trim() || undefined,
        admin_password: form.admin_password.trim() || undefined,
      });
      onCreated({ admin_email: r.data.admin?.email, temp_password: r.data.admin?.temp_password });
    } catch (err) { setError(fmtErr(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-2xl" testid="ua-create-org-modal">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="noam-overline">Nouvelle organisation</div>
          <div className="noam-display text-lg font-semibold mt-1">Provisionner un nouvel environnement</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Crée l'organisation, le compte super_admin client et applique le plan initial.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-[11px] text-zinc-400 mb-1 block">Nom de l'organisation <span className="text-[#E24B4A]">*</span></label>
          <input autoFocus required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Ex : Banque Privée Lémanique"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ua-create-name" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Secteur</label>
            <select value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none">
              <option value="">—</option>
              {["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Plan initial</label>
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ua-create-plan">
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="space-y-3 pt-3 border-t border-white/5">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Super Admin client (recommandé)</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-zinc-400 mb-1 block">Email</label>
              <input type="email" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })}
                placeholder="admin@client.ch"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none" data-testid="ua-create-admin-email" />
            </div>
            <div>
              <label className="text-[11px] text-zinc-400 mb-1 block">Mot de passe (auto si vide)</label>
              <input value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
                placeholder="auto-généré"
                className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:border-[#378ADD] focus:outline-none" />
            </div>
          </div>
        </div>
        {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="ua-create-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Création…</> : <>Créer l'organisation</>}
        </button>
      </form>
    </Modal>
  );
}

function ModuleEditorModal({ org, available, planModules, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(org.modules_enabled || []);
  const [locked, setLocked] = useState(org.modules_locked || []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState(""); // "" | "on" | "off"
  const [search, setSearch] = useState("");

  const planSet = new Set(planModules);

  // Toggle effective ON/OFF — handles all 4 cases transparently
  const toggleEffective = (k) => {
    const inPlan = planSet.has(k);
    const isLocked = locked.includes(k);
    const isExtra = enabled.includes(k);
    const isOn = (inPlan || isExtra) && !isLocked;
    if (isOn) {
      // Turn OFF
      if (inPlan) setLocked((arr) => [...arr, k]);
      else setEnabled((arr) => arr.filter((x) => x !== k));
    } else {
      // Turn ON
      if (isLocked) setLocked((arr) => arr.filter((x) => x !== k));
      if (!inPlan && !isExtra) setEnabled((arr) => [...arr, k]);
    }
  };

  const save = async () => {
    setBusy(true); setError("");
    try {
      await api.patch(`/ultra-admin/organisations/${org.id}/modules`, { modules_enabled: enabled, modules_locked: locked });
      onSaved();
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Build rows with computed state + filtering
  const rows = Object.entries(available).map(([k, label]) => {
    const inPlan = planSet.has(k);
    const isLocked = locked.includes(k);
    const isExtra = enabled.includes(k);
    const isOn = (inPlan || isExtra) && !isLocked;
    let source;
    if (isOn && inPlan) source = { label: "via plan", color: "#1D9E75" };
    else if (isOn && isExtra) source = { label: "ajouté en surplus", color: "#378ADD" };
    else if (!isOn && inPlan && isLocked) source = { label: "désactivé manuellement", color: "#EF9F27" };
    else source = { label: "hors plan", color: "#52525b" };
    return { key: k, label, inPlan, isLocked, isExtra, isOn, source };
  }).filter((r) => {
    if (filter === "on" && !r.isOn) return false;
    if (filter === "off" && r.isOn) return false;
    if (search) {
      const s = search.toLowerCase();
      if (!r.label.toLowerCase().includes(s) && !r.key.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  const totalOn = Object.keys(available).filter((k) => {
    const inPlan = planSet.has(k);
    const isLocked = locked.includes(k);
    const isExtra = enabled.includes(k);
    return (inPlan || isExtra) && !isLocked;
  }).length;

  // Reset to plan defaults (clear all overrides)
  const resetToPlan = () => {
    if (!window.confirm("Réinitialiser ? Cela supprimera tous tes overrides (surplus + désactivations manuelles) et reviendra à la couverture standard du plan.")) return;
    setEnabled([]);
    setLocked([]);
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-3xl" testid="ua-modules-modal">
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="noam-overline">Modules · {org.name}</div>
          <div className="noam-display text-base font-semibold mt-0.5">Plan {org.plan} — couverture & overrides</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">
            Un seul interrupteur par module : ON/OFF effectif pour cette org. Tu peux désactiver des modules du plan ou en activer hors plan, sans changer le plan lui-même.
          </p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5" data-testid="ua-modules-close"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          type="text"
          placeholder="Filtrer par nom…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="noam-input text-xs flex-1 min-w-[200px]"
          data-testid="ua-modules-search"
        />
        <div className="flex items-center gap-1 text-[11px]">
          <button onClick={() => setFilter("")} className={`px-2 py-1 rounded ${filter === "" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="ua-modules-filter-all">Tous ({Object.keys(available).length})</button>
          <button onClick={() => setFilter("on")} className={`px-2 py-1 rounded ${filter === "on" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="ua-modules-filter-on">Activés ({totalOn})</button>
          <button onClick={() => setFilter("off")} className={`px-2 py-1 rounded ${filter === "off" ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`} data-testid="ua-modules-filter-off">Désactivés ({Object.keys(available).length - totalOn})</button>
        </div>
        <button onClick={resetToPlan} className="text-[11px] text-zinc-400 hover:text-amber-400 px-2 py-1 rounded inline-flex items-center gap-1" data-testid="ua-modules-reset">
          <RefreshCw className="w-3 h-3" /> Réinitialiser au plan
        </button>
      </div>

      <div className="border border-white/5 rounded-md overflow-hidden max-h-[55vh] overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="text-zinc-500 sticky top-0 bg-[var(--noam-card-bg)]">
            <tr className="border-b border-white/5">
              <th className="text-left p-2 w-1/2">Module</th>
              <th className="text-left p-2">Source</th>
              <th className="text-center p-2 w-28">État</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className={`border-b border-white/5 ${r.isOn ? "" : "opacity-70"}`} data-testid={`ua-mod-${r.key}`}>
                <td className="p-2 text-white">{r.label}</td>
                <td className="p-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: r.source.color, background: `${r.source.color}1f` }}>
                    {r.source.label}
                  </span>
                </td>
                <td className="p-2 text-center">
                  <button
                    onClick={() => toggleEffective(r.key)}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
                      r.isOn
                        ? "bg-[#1D9E75]/20 text-[#1D9E75] hover:bg-[#1D9E75]/30"
                        : "bg-zinc-700/30 text-zinc-400 hover:bg-zinc-700/50"
                    }`}
                    data-testid={`ua-mod-toggle-${r.key}`}
                  >
                    {r.isOn ? "Activé" : "Désactivé"}
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={3} className="p-4 text-center text-zinc-500 text-xs">Aucun module ne correspond.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2 mt-3">{error}</div>}
      <div className="flex justify-between items-center mt-4">
        <div className="text-[11px] text-zinc-500">{totalOn} module(s) actif(s) sur {Object.keys(available).length}</div>
        <div className="flex gap-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5">Annuler</button>
          <button onClick={save} disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="ua-modules-save">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Enregistrement…</> : <><Check className="w-3.5 h-3.5" /> Enregistrer</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const PILOT_STATUS = {
  actif: { label: "Actif", color: "#1D9E75" },
  dormant: { label: "Dormant", color: "#EF9F27" },
  inactif: { label: "Inactif", color: "#E24B4A" },
  jamais: { label: "Jamais connecté", color: "#71717A" },
};

function fmtPilotDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

// V23.16.x — Suivi d'activité des pilotes : dernière connexion + usage cockpit.
function PilotsPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/ultra-admin/pilot-activity");
      setData(r.data.items || []);
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); setData([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = { actif: 0, dormant: 0, inactif: 0, jamais: 0 };
  (data || []).forEach((p) => { counts[p.status] = (counts[p.status] || 0) + 1; });

  return (
    <div className="space-y-4" data-testid="ua-pilots-panel">
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-center gap-3 flex-wrap">
        {Object.entries(PILOT_STATUS).map(([k, v]) => (
          <div key={k} className="noam-card px-4 py-2.5 flex items-center gap-2" data-testid={`ua-pilots-count-${k}`}>
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: v.color }} />
            <span className="text-sm font-semibold text-white">{counts[k] || 0}</span>
            <span className="text-xs text-zinc-400">{v.label}</span>
          </div>
        ))}
        <button onClick={load} className="btn-ghost text-xs ml-auto" data-testid="ua-pilots-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Rafraîchir
        </button>
      </div>
      {data === null && (
        <div className="noam-card p-10 text-center text-zinc-400 text-sm">
          <Loader2 className="w-5 h-5 animate-spin mx-auto" />
        </div>
      )}
      {data && (
        <div className="noam-card p-0 overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/5 bg-white/[0.02]">
              <tr>
                <th className="text-left py-3 px-4 font-normal">Pilote</th>
                <th className="text-left font-normal">Organisation</th>
                <th className="text-left font-normal">Plan</th>
                <th className="text-left font-normal">Dernière connexion</th>
                <th className="text-left font-normal">Dernière activité</th>
                <th className="text-center font-normal">Usage environnement</th>
                <th className="text-center font-normal">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.map((p) => {
                const st = PILOT_STATUS[p.status] || PILOT_STATUS.jamais;
                const usageTip = Object.entries(p.usage || {})
                  .filter(([, v]) => v > 0)
                  .map(([k, v]) => `${k}: ${v}`)
                  .join(" · ") || "Aucun objet créé";
                return (
                  <tr key={p.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ua-pilot-row-${p.id}`}>
                    <td className="py-3 px-4">
                      <div className="text-white">{p.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{p.email}</div>
                    </td>
                    <td className="text-zinc-300">{p.org_name || "—"}</td>
                    <td>
                      <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300 uppercase">{p.plan || "—"}</span>
                    </td>
                    <td className="text-zinc-300">
                      {fmtPilotDate(p.last_login)}
                      {p.days_since_activity != null && p.last_login && (
                        <span className="text-[10px] text-zinc-500 ml-1.5">(il y a {p.days_since_activity} j)</span>
                      )}
                    </td>
                    <td className="text-zinc-400">{fmtPilotDate(p.last_seen_at)}</td>
                    <td className="text-center">
                      <span className="text-white font-semibold" title={usageTip} data-testid={`ua-pilot-usage-${p.id}`}>
                        {p.usage_total}
                      </span>
                      <span className="text-[10px] text-zinc-500 ml-1">objets</span>
                    </td>
                    <td className="text-center">
                      <span className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wide"
                        style={{ background: `${st.color}1F`, color: st.color, border: `1px solid ${st.color}40` }}
                        data-testid={`ua-pilot-status-${p.id}`}>
                        {st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && (
                <tr><td colSpan={7} className="py-10 text-center text-zinc-500 text-sm">Aucun pilote trouvé.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


function UsersPanel() {
  const [users, setUsers] = useState(null);
  const [orgs, setOrgs] = useState([]); // V23.15.3 — liste pour selects
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [tempCred, setTempCred] = useState(null);
  const [showCreate, setShowCreate] = useState(false); // V23.15.3
  const [editingMembership, setEditingMembership] = useState(null); // V23.15.3

  const load = async () => {
    try {
      const [u, o] = await Promise.all([
        api.get("/ultra-admin/users"),
        api.get("/ultra-admin/organisations"),
      ]);
      setUsers(u.data.items);
      setOrgs(o.data.organisations || []);
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startEdit = (u) => {
    setEditingId(u.id);
    setDraft({ email: u.email, name: u.name || "", organisation: u.organisation || "", active: u.active });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/ultra-admin/users/${editingId}`, draft);
      setEditingId(null); await load();
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte ${u.email} ?`)) return;
    try { await api.delete(`/ultra-admin/users/${u.id}`); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };
  const reset = async (u) => {
    if (!window.confirm(`Réinitialiser le mot de passe de ${u.email} ?`)) return;
    try {
      const r = await api.post(`/ultra-admin/users/${u.id}/reset-password`);
      setTempCred({ email: u.email, password: r.data.temp_password });
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
  };

  return (
    <>
      <div className="flex justify-end">
        <button onClick={() => setShowCreate(true)} className="btn-primary text-xs" data-testid="ua-user-create-btn">
          <UserPlus className="w-3.5 h-3.5" /> Créer un utilisateur
        </button>
      </div>
      {error && <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs"><AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span><button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button></div>}
      <div className="noam-card p-0 overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-white/5 text-[10px] uppercase tracking-wider text-zinc-500">
          <div className="col-span-3">Email</div>
          <div className="col-span-2">Nom</div>
          <div className="col-span-3">Organisations (rôle)</div>
          <div className="col-span-1 text-center">Statut</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>
        {!users && <div className="px-5 py-6 text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>}
        {users?.map((u) => {
          const editing = editingId === u.id;
          return (
            <div key={u.id} className="grid grid-cols-12 gap-3 items-center px-5 py-3 border-b border-white/5 text-sm hover:bg-white/[0.02]" data-testid={`ua-user-row-${u.id}`}>
              <div className="col-span-3 truncate">
                {editing ? (
                  <input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className="noam-input text-xs" />
                ) : (
                  <>
                    <span className="text-white">{u.email}</span>
                    {u.is_ultra_admin && <span className="ml-2 text-[10px] text-[#EF9F27] font-semibold">★ ULTRA</span>}
                  </>
                )}
              </div>
              <div className="col-span-2 text-zinc-400 truncate">
                {editing ? <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="noam-input text-xs" /> : (u.name || "—")}
              </div>
              <div className="col-span-3 text-[11px] text-zinc-400">
                {(u.memberships || []).map((m) => (
                  <div key={m.org_id} className="truncate flex items-center gap-1">
                    <span className="text-zinc-200">{m.org_name || m.org_id}</span>
                    <span className="text-[#378ADD]">· {m.role}</span>
                    <button onClick={() => setEditingMembership({ user: u, org_id: m.org_id, current_role: m.role })}
                      className="text-zinc-500 hover:text-white" title="Modifier le rôle" data-testid={`ua-user-edit-membership-${u.id}-${m.org_id}`}>
                      <Pencil className="w-2.5 h-2.5" />
                    </button>
                  </div>
                ))}
                {(!u.memberships || u.memberships.length === 0) && <span className="italic text-zinc-600">aucune membership</span>}
                <button onClick={() => setEditingMembership({ user: u, org_id: null, current_role: "lecteur" })}
                  className="mt-1 text-[10px] text-[#378ADD] hover:underline inline-flex items-center gap-0.5" data-testid={`ua-user-add-membership-${u.id}`}>
                  <Plus className="w-2.5 h-2.5" /> ajouter
                </button>
              </div>
              <div className="col-span-1 text-center">
                {editing ? (
                  <select value={String(draft.active)} onChange={(e) => setDraft({ ...draft, active: e.target.value === "true" })} className="noam-input text-xs">
                    <option value="true">Actif</option>
                    <option value="false">Désactivé</option>
                  </select>
                ) : (
                  <span className={`text-[10px] ${u.active ? "text-[#1D9E75]" : "text-zinc-500"}`}>{u.active ? "actif" : "désactivé"}</span>
                )}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                {editing ? (
                  <>
                    <button onClick={saveEdit} className="text-[#1D9E75] p-1.5 rounded hover:bg-white/5" title="Enregistrer"><Check className="w-3.5 h-3.5" /></button>
                    <button onClick={() => setEditingId(null)} className="text-zinc-500 p-1.5 rounded hover:bg-white/5" title="Annuler"><X className="w-3.5 h-3.5" /></button>
                  </>
                ) : (
                  <>
                    <button onClick={() => reset(u)} className="text-zinc-400 hover:text-[#EF9F27] p-1.5 rounded hover:bg-white/5" title="Reset password" data-testid={`ua-user-reset-${u.id}`}><KeyRound className="w-3.5 h-3.5" /></button>
                    <button onClick={() => startEdit(u)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Modifier" data-testid={`ua-user-edit-${u.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => remove(u)} className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5" title="Supprimer" data-testid={`ua-user-delete-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {tempCred && <TempCredView cred={tempCred} onClose={() => setTempCred(null)} />}
      {showCreate && (
        <CreateUserUltraModal
          orgs={orgs}
          onClose={() => setShowCreate(false)}
          onCreated={(cred) => {
            setShowCreate(false);
            if (cred?.temp_password) setTempCred({ email: cred.email, password: cred.temp_password });
            load();
          }}
        />
      )}
      {editingMembership && (
        <EditMembershipModal
          payload={editingMembership}
          orgs={orgs}
          onClose={() => setEditingMembership(null)}
          onSaved={() => { setEditingMembership(null); load(); }}
        />
      )}
    </>
  );
}

// V23.15.3 — Modal de création d'utilisateur avec rôle workspace + membership
function CreateUserUltraModal({ orgs, onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "", name: "", password: "",
    role: "lecteur", global_role: "user", organisation_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const payload = { ...form };
      if (!payload.organisation_id) delete payload.organisation_id;
      if (!payload.password) delete payload.password;
      const r = await api.post("/ultra-admin/users", payload);
      notify.success("Compte créé");
      onCreated?.(r.data);
    } catch (e2) {
      setError(fmtErr(e2?.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ua-user-create-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="noam-card max-w-lg w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">Créer un utilisateur</h3>
          <button type="button" onClick={onClose} className="text-zinc-400"><X className="w-4 h-4" /></button>
        </div>
        {error && <div className="text-xs text-[#FCA5A5] border border-[#E24B4A]/30 bg-[#E24B4A]/10 p-2 rounded">{error}</div>}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Email *</label>
          <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="noam-input w-full" required data-testid="ua-user-create-email" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Nom</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="noam-input w-full" data-testid="ua-user-create-name" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Mot de passe (vide = auto)</label>
            <input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="noam-input w-full" data-testid="ua-user-create-pwd" />
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Organisation (optionnelle)</label>
          <select value={form.organisation_id} onChange={(e) => setForm({ ...form, organisation_id: e.target.value })} className="noam-input w-full" data-testid="ua-user-create-org">
            <option value="">— Aucune (utilisateur sans membership) —</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rôle workspace</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="noam-input w-full" data-testid="ua-user-create-role">
              <option value="lecteur">Lecteur — lecture seule</option>
              <option value="contributeur">Contributeur — écrit ses propres données</option>
              <option value="admin">Admin — gère l&apos;organisation</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rôle plateforme</label>
            <select value={form.global_role} onChange={(e) => setForm({ ...form, global_role: e.target.value })} className="noam-input w-full" data-testid="ua-user-create-global-role">
              <option value="user">user (standard)</option>
              <option value="admin">admin (gère ses orgs)</option>
            </select>
          </div>
        </div>
        <p className="text-[10px] text-zinc-500">Le rôle workspace ne s&apos;applique que si une organisation est sélectionnée. Pour créer un Ultra Admin, modifie le flag <code>is_super_admin</code> directement en base après création.</p>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-secondary text-xs">Annuler</button>
          <button type="submit" disabled={busy} className="btn-primary text-xs" data-testid="ua-user-create-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserPlus className="w-3 h-3" />} Créer
          </button>
        </div>
      </form>
    </div>
  );
}

// V23.15.3 — Modal d'édition de membership (rôle workspace pour une org)
function EditMembershipModal({ payload, orgs, onClose, onSaved }) {
  const [form, setForm] = useState({
    org_id: payload.org_id || "",
    role: payload.current_role || "lecteur",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isExisting = !!payload.org_id;

  const submit = async (action) => {
    if (!form.org_id) { setError("Organisation requise"); return; }
    setBusy(true); setError("");
    try {
      await api.patch(`/ultra-admin/users/${payload.user.id}/membership`, {
        org_id: form.org_id,
        role: form.role,
        action,
      });
      notify.success(action === "remove" ? "Membership retirée" : "Membership mise à jour");
      onSaved?.();
    } catch (e2) {
      setError(fmtErr(e2?.response?.data?.detail) || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ua-user-membership-modal">
      <div onClick={(e) => e.stopPropagation()} className="noam-card max-w-md w-full p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold">{isExisting ? "Modifier la membership" : "Ajouter une membership"}</h3>
          <button onClick={onClose} className="text-zinc-400"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs text-zinc-400 truncate">{payload.user.email}</p>
        {error && <div className="text-xs text-[#FCA5A5] border border-[#E24B4A]/30 bg-[#E24B4A]/10 p-2 rounded">{error}</div>}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Organisation</label>
          <select value={form.org_id} onChange={(e) => setForm({ ...form, org_id: e.target.value })}
            disabled={isExisting} className="noam-input w-full" data-testid="ua-membership-org">
            <option value="">— Sélectionner —</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500">Rôle</label>
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="noam-input w-full" data-testid="ua-membership-role">
            <option value="lecteur">Lecteur — lecture seule</option>
            <option value="contributeur">Contributeur — écrit ses propres données</option>
            <option value="admin">Admin — gère l&apos;organisation</option>
          </select>
        </div>
        <div className="flex justify-between gap-2 pt-2">
          {isExisting && (
            <button onClick={() => submit("remove")} disabled={busy} className="text-xs text-[#E24B4A] hover:underline" data-testid="ua-membership-remove">
              Retirer cette membership
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="btn-secondary text-xs">Annuler</button>
            <button onClick={() => submit("upsert")} disabled={busy} className="btn-primary text-xs" data-testid="ua-membership-save">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Enregistrer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function TempCredView({ cred, onClose }) {
  const copy = () => navigator.clipboard?.writeText(`${cred.email} / ${cred.password}`).catch(() => {});
  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="ua-temp-cred">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="noam-overline text-[#EF9F27]">À copier maintenant</div>
          <div className="noam-display text-base font-semibold mt-0.5">Identifiants temporaires</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <div className="rounded-md border border-[#EF9F27]/40 bg-[#EF9F27]/10 p-4 space-y-1">
        <div className="text-[11px] text-[#FCD9A2] uppercase">Email</div>
        <div className="text-white font-mono">{cred.email}</div>
        <div className="text-[11px] text-[#FCD9A2] uppercase mt-3">Mot de passe</div>
        <div className="text-white font-mono break-all">{cred.password}</div>
      </div>
      <p className="text-[11px] text-zinc-500 mt-3">Ce mot de passe ne sera plus visible après fermeture.</p>
      <button onClick={copy} className="btn-primary w-full mt-4 justify-center"><span>Copier</span></button>
    </Modal>
  );
}

function AccessRequestsPanel() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("pending"); // pending | approved | rejected | all
  const [busyId, setBusyId] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [tempCred, setTempCred] = useState(null);

  const load = async () => {
    setItems(null);
    try {
      const params = filter === "all" ? {} : { params: { status: filter } };
      const r = await api.get("/ultra-admin/access-requests", params);
      setItems(r.data.items || []);
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail));
      setItems([]);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const del = async (req) => {
    if (!window.confirm(`Supprimer définitivement la demande de ${req.email} ?`)) return;
    setBusyId(req.id);
    try { await api.delete(`/ultra-admin/access-requests/${req.id}`); await load(); }
    catch (e) { setError(fmtErr(e?.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso || "—"; }
  };

  const statusBadge = (s) => {
    const map = {
      pending: { label: "En attente", color: "#EF9F27" },
      approved: { label: "Approuvée", color: "#1D9E75" },
      rejected: { label: "Rejetée", color: "#A1A1AA" },
    };
    const m = map[s] || map.pending;
    return <span className="text-[10px] px-2 py-0.5 rounded" style={{ background: `${m.color}1A`, color: m.color, border: `1px solid ${m.color}40` }}>{m.label}</span>;
  };

  return (
    <div className="space-y-4" data-testid="ua-requests-panel">
      <div className="flex items-center gap-2">
        <Inbox className="w-4 h-4 text-[#EF9F27]" />
        <span className="noam-overline">Demandes d'accès — site public</span>
        <div className="ml-auto flex items-center gap-1 text-xs">
          {["pending", "approved", "rejected", "all"].map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`ua-req-filter-${s}`}
              className={`px-2.5 py-1 rounded-md transition-colors ${filter === s ? "bg-white/10 text-white" : "text-zinc-400 hover:text-zinc-200"}`}>
              {s === "pending" ? "En attente" : s === "approved" ? "Approuvées" : s === "rejected" ? "Rejetées" : "Toutes"}
            </button>
          ))}
        </div>
      </div>
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      {items === null && (
        <div className="noam-card p-8 text-center text-zinc-400 text-sm flex items-center justify-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      )}
      {items && items.length === 0 && (
        <div className="noam-card p-10 text-center" data-testid="ua-req-empty">
          <Mail className="w-8 h-8 mx-auto text-zinc-600" />
          <div className="text-zinc-400 text-sm mt-3">Aucune demande {filter !== "all" ? `« ${filter} »` : ""} pour l'instant.</div>
          <div className="text-[11px] text-zinc-600 mt-1">Les demandes faites depuis <span className="font-mono">/request-access</span> apparaîtront ici.</div>
        </div>
      )}
      {items && items.length > 0 && (
        <div className="noam-card p-0 overflow-hidden">
          {items.map((r) => (
            <div key={r.id} className="px-5 py-4 border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ua-req-row-${r.id}`}>
              <div className="flex items-start gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-white text-sm font-medium">{r.nom || "—"}</span>
                    <span className="text-zinc-500 text-xs">·</span>
                    <span className="text-zinc-300 text-xs">{r.organisation || "—"}</span>
                    {statusBadge(r.status)}
                    <span className="ml-auto text-[10px] text-zinc-500">{fmtDate(r.created_at)}</span>
                  </div>
                  <div className="text-[11px] text-[#378ADD] font-mono mt-1">{r.email}</div>
                  {r.contexte && (
                    <p className="text-xs text-zinc-300 mt-2 leading-relaxed bg-white/[0.02] border border-white/5 rounded p-2.5">
                      {r.contexte}
                    </p>
                  )}
                  {r.rejection_reason && (
                    <div className="text-[11px] text-zinc-500 mt-2 italic">Motif de rejet : {r.rejection_reason}</div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {r.status === "pending" && (
                    <>
                      <button onClick={() => setApproveTarget(r)} disabled={busyId === r.id}
                        className="btn-primary text-xs disabled:opacity-50" data-testid={`ua-req-approve-${r.id}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Approuver
                      </button>
                      <button onClick={() => setRejectTarget(r)} disabled={busyId === r.id}
                        className="btn-ghost text-xs disabled:opacity-50" data-testid={`ua-req-reject-${r.id}`}>
                        <ShieldX className="w-3.5 h-3.5" /> Rejeter
                      </button>
                    </>
                  )}
                  <button onClick={() => del(r)} disabled={busyId === r.id}
                    className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5"
                    title="Supprimer la demande" data-testid={`ua-req-delete-${r.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {approveTarget && (
        <ApproveRequestModal
          req={approveTarget}
          onClose={() => setApproveTarget(null)}
          onApproved={(cred) => {
            setApproveTarget(null);
            if (cred?.admin?.temp_password) setTempCred({ email: cred.admin.email, password: cred.admin.temp_password });
            load();
          }}
        />
      )}
      {rejectTarget && (
        <RejectRequestModal
          req={rejectTarget}
          onClose={() => setRejectTarget(null)}
          onRejected={() => { setRejectTarget(null); load(); }}
        />
      )}
      {tempCred && <TempCredView cred={tempCred} onClose={() => setTempCred(null)} />}
    </div>
  );
}

function ApproveRequestModal({ req, onClose, onApproved }) {
  const [plan, setPlan] = useState("test");
  const [sector, setSector] = useState("");
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [availablePlans, setAvailablePlans] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/ultra-admin/plans");
        setAvailablePlans((r.data.items || []).map((p) => ({ id: p.id, name: p.name })));
      } catch { /* keep fallback to hardcoded list */ }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const r = await api.post(`/ultra-admin/access-requests/${req.id}/approve`, {
        plan,
        sector: sector || null,
        admin_password: pwd.trim() || null,
      });
      onApproved(r.data);
    } catch (er) { setError(fmtErr(er?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const planOptions = availablePlans.length > 0
    ? availablePlans
    : Object.keys(PLAN_LABELS).map((p) => ({ id: p, name: PLAN_LABELS[p].label }));

  return (
    <Modal onClose={onClose} maxWidth="max-w-xl" testid="ua-req-approve-modal">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="noam-overline text-[#1D9E75]">Approuver la demande</div>
          <div className="noam-display text-lg font-semibold mt-1">Provisionner « {req.organisation} »</div>
          <p className="text-[11px] text-zinc-500 mt-1.5">Crée une organisation + compte admin pour <span className="font-mono text-zinc-300">{req.email}</span>.</p>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Plan initial</label>
            <select value={plan} onChange={(e) => setPlan(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
              data-testid="ua-req-approve-plan">
              {planOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 mb-1 block">Secteur</label>
            <select value={sector} onChange={(e) => setSector(e.target.value)}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none">
              <option value="">—</option>
              {["Banque", "Assurance", "Santé", "Industrie", "PME", "Autre"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-[11px] text-zinc-400 mb-1 block">Mot de passe admin (auto si vide)</label>
          <input value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder="auto-généré"
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono focus:border-[#378ADD] focus:outline-none"
            data-testid="ua-req-approve-pwd" />
          <p className="text-[10px] text-zinc-600 mt-1">Affiché à l'écran après création — à transmettre au demandeur.</p>
        </div>
        {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="ua-req-approve-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Approbation…</> : <><ShieldCheck className="w-4 h-4" /> Provisionner et générer les identifiants</>}
        </button>
      </form>
    </Modal>
  );
}

function RejectRequestModal({ req, onClose, onRejected }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post(`/ultra-admin/access-requests/${req.id}/reject`, { reason: reason.trim() || null });
      onRejected();
    } catch (er) { setError(fmtErr(er?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose} maxWidth="max-w-md" testid="ua-req-reject-modal">
      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="noam-overline text-zinc-400">Rejeter la demande</div>
          <div className="noam-display text-base font-semibold mt-1">{req.email}</div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-[11px] text-zinc-400 mb-1 block">Motif (optionnel, interne)</label>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="Ex : profil hors cible commerciale."
            className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:border-[#378ADD] focus:outline-none"
            data-testid="ua-req-reject-reason" />
        </div>
        {error && <div className="text-xs text-[#E24B4A] bg-[#E24B4A]/10 border border-[#E24B4A]/30 rounded px-3 py-2">{error}</div>}
        <button type="submit" disabled={busy} className="btn-ghost w-full justify-center text-sm disabled:opacity-50" data-testid="ua-req-reject-submit">
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Rejet…</> : <><ShieldX className="w-4 h-4" /> Confirmer le rejet</>}
        </button>
      </form>
    </Modal>
  );
}

function PlansPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // "new" | plan object

  const load = async () => {
    try {
      const r = await api.get("/ultra-admin/plans");
      setData(r.data);
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail));
      setData({ items: [], available_modules: {} });
    }
  };
  useEffect(() => { load(); }, []);

  const duplicate = async (p) => {
    try {
      await api.post(`/ultra-admin/plans/${p.id}/duplicate`);
      notify.success(`Plan « ${p.name} » dupliqué.`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le plan « ${p.name} » ?`)) return;
    try {
      await api.delete(`/ultra-admin/plans/${p.id}`);
      notify.success("Plan supprimé.");
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  if (!data) {
    return (
      <div className="noam-card p-10 text-center text-zinc-400 text-sm flex items-center justify-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement des plans…
      </div>
    );
  }
  const { items, available_modules } = data;

  return (
    <div className="space-y-4" data-testid="ua-plans-panel">
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-[#7F77DD]" />
        <span className="noam-overline">Plans & modules</span>
        <span className="text-[11px] text-zinc-500 ml-2">{items.length} plan{items.length > 1 ? "s" : ""} configurés</span>
        <button onClick={() => setEditing("new")} className="btn-primary text-xs ml-auto" data-testid="ua-plans-create-btn">
          <Plus className="w-3.5 h-3.5" /> Nouveau plan
        </button>
      </div>
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="grid lg:grid-cols-2 gap-4" data-testid="ua-plans-grid">
        {items.map((p) => (
          <div key={p.id} className="noam-card p-5" data-testid={`ua-plan-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="noam-display text-lg font-semibold">{p.name}</h3>
                  {p.is_default && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-[#7F77DD]/15 text-[#7F77DD] border border-[#7F77DD]/30 uppercase tracking-wider">
                      Par défaut
                    </span>
                  )}
                  <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-400 font-mono">{p.id}</span>
                </div>
                {p.description && <p className="text-zinc-400 text-xs mt-1">{p.description}</p>}
                <div className="text-[10px] text-zinc-500 mt-2 flex items-center gap-3">
                  <span><strong className="text-zinc-300">{p.modules_enabled?.length || 0}</strong> module(s)</span>
                  <span>·</span>
                  <span><strong className="text-zinc-300">{p.org_count || 0}</strong> organisation(s)</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setEditing(p)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Modifier" data-testid={`ua-plan-edit-${p.id}`}>
                  <Edit2 className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => duplicate(p)} className="text-zinc-400 hover:text-white p-1.5 rounded hover:bg-white/5" title="Dupliquer" data-testid={`ua-plan-duplicate-${p.id}`}>
                  <Layers className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(p)}
                  disabled={p.is_default || (p.org_count || 0) > 0}
                  className="text-zinc-500 hover:text-[#E24B4A] p-1.5 rounded hover:bg-white/5 disabled:opacity-30 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                  title={p.is_default ? "Plan par défaut — non supprimable" : (p.org_count > 0 ? `${p.org_count} organisation(s) sur ce plan` : "Supprimer")}
                  data-testid={`ua-plan-delete-${p.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {(p.modules_enabled || []).slice(0, 24).map((m) => (
                <span key={m} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-300 border border-white/10">
                  {available_modules[m] || m}
                </span>
              ))}
              {(p.modules_enabled || []).length === 0 && (
                <span className="text-[11px] text-zinc-500 italic">Aucun module — composition libre via les organisations.</span>
              )}
            </div>
          </div>
        ))}
      </div>
      {editing && (
        <PlanEditorModal
          plan={editing === "new" ? null : editing}
          availableModules={available_modules}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function PlanEditorModal({ plan, availableModules, onClose, onSaved }) {
  const isEdit = !!plan;
  const [form, setForm] = useState(() => plan ? {
    name: plan.name || "",
    description: plan.description || "",
    modules_enabled: new Set(plan.modules_enabled || []),
  } : { name: "", description: "", modules_enabled: new Set() });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (m) => setForm((f) => {
    const next = new Set(f.modules_enabled);
    if (next.has(m)) next.delete(m); else next.add(m);
    return { ...f, modules_enabled: next };
  });
  const toggleAll = (on) => setForm((f) => ({ ...f, modules_enabled: on ? new Set(Object.keys(availableModules)) : new Set() }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        modules_enabled: Array.from(form.modules_enabled),
      };
      if (isEdit) {
        await api.patch(`/ultra-admin/plans/${plan.id}`, payload);
        notify.success(`Plan « ${payload.name} » mis à jour.`);
      } else {
        await api.post("/ultra-admin/plans", payload);
        notify.success(`Plan « ${payload.name} » créé.`);
      }
      onSaved();
    } catch (e) { setErr(fmtError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="ua-plan-editor-modal">
        <div className="flex items-start justify-between p-5 border-b border-white/5">
          <div>
            <div className="noam-overline">{isEdit ? "Modifier le plan" : "Nouveau plan"}</div>
            <div className="noam-display text-base font-semibold mt-0.5">{isEdit ? plan.name : "Plan personnalisé"}</div>
            {isEdit && plan.is_default && (
              <div className="text-[11px] text-[#EF9F27] mt-1">Plan par défaut — modifiable mais non supprimable.</div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="overflow-y-auto p-5 space-y-4">
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Nom du plan *</label>
            <input required autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Ex : Plan Sur-mesure Suisse" className="noam-input" data-testid="ua-plan-form-name" />
          </div>
          <div>
            <label className="text-[11px] text-zinc-400 uppercase tracking-wider mb-1 block">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
              placeholder="À qui s'adresse ce plan, dans quel contexte." className="noam-input"
              data-testid="ua-plan-form-description" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] text-zinc-400 uppercase tracking-wider">Modules inclus</label>
              <div className="flex items-center gap-2 text-[11px]">
                <span className="text-zinc-500">{form.modules_enabled.size} / {Object.keys(availableModules).length}</span>
                <button type="button" onClick={() => toggleAll(true)} className="text-[#378ADD] hover:text-white" data-testid="ua-plan-toggle-all">Tout cocher</button>
                <span className="text-zinc-700">·</span>
                <button type="button" onClick={() => toggleAll(false)} className="text-zinc-400 hover:text-white" data-testid="ua-plan-toggle-none">Aucun</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {Object.entries(availableModules).map(([key, label]) => {
                const on = form.modules_enabled.has(key);
                return (
                  <button
                    key={key} type="button" onClick={() => toggle(key)}
                    className={`flex items-center justify-between text-left px-3 py-2 rounded-md border transition-colors ${on ? "border-[#7F77DD]/40 bg-[#7F77DD]/10" : "border-white/10 hover:border-white/25 bg-white/[0.02]"}`}
                    data-testid={`ua-plan-module-${key}`}
                  >
                    <div className="min-w-0">
                      <div className="text-xs text-white truncate">{label}</div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{key}</div>
                    </div>
                    <div className={`shrink-0 w-9 h-5 rounded-full p-0.5 transition-colors ${on ? "bg-[#7F77DD]" : "bg-zinc-700"}`}>
                      <div className={`w-4 h-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {err && (
            <div className="flex items-start gap-2 p-2.5 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /> {err}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/5 sticky bottom-0 bg-[var(--noam-bg)]">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">Annuler</button>
            <button type="submit" disabled={busy} className="btn-primary text-xs disabled:opacity-50" data-testid="ua-plan-form-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enregistrement…</> : <><Check className="w-3.5 h-3.5" /> {isEdit ? "Enregistrer" : "Créer le plan"}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}




function UltraTrendPanel() {
  const [sources, setSources] = useState(null);
  const [signals, setSignals] = useState([]);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [pushing, setPushing] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [filter, setFilter] = useState({ level: "", analyzed: "" });

  const load = async () => {
    try {
      const [s1, s2, o] = await Promise.all([
        api.get("/ultra-admin/trend/sources"),
        api.get("/ultra-admin/trend/signals?limit=120"),
        api.get("/ultra-admin/organisations"),
      ]);
      setSources(s1.data.items || []);
      setSignals(s2.data.items || []);
      setOrgs(o.data.organisations || []);
    } catch (e) { setError(fmtErr(e?.response?.data?.detail)); setSources([]); }
  };
  useEffect(() => { load(); }, []);

  const refreshAll = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/ultra-admin/trend/sources/refresh-all");
      const total = r.data.refreshed.reduce((acc, s) => acc + (s.inserted || 0), 0);
      notify.success(`Veille rafraîchie — ${total} nouveau(x) signal(aux).`);
      await load();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setRefreshing(false); }
  };

  const analyze = async (sig) => {
    setAnalyzing(sig.id);
    try {
      const r = await api.post(`/ultra-admin/trend/signals/${sig.id}/analyze`);
      setSignals((arr) => arr.map((s) => (s.id === sig.id ? r.data : s)));
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAnalyzing(null); }
  };

  const filtered = signals.filter((s) => {
    if (filter.level && s.level !== filter.level) return false;
    if (filter.analyzed === "yes" && !s.analyzed) return false;
    if (filter.analyzed === "no" && s.analyzed) return false;
    return true;
  });

  if (!sources) {
    return <div className="noam-card p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="ua-trend-panel">
      {error && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-xs">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5" /><span>{error}</span>
          <button className="ml-auto" onClick={() => setError(null)}><X className="w-3 h-3" /></button>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <ActivityIcon className="w-4 h-4 text-[#378ADD]" />
        <span className="noam-overline">Veille Constante Partners</span>
        <span className="text-[11px] text-zinc-500 ml-2">{sources.length} source(s) · {signals.length} signal(aux)</span>
        <button onClick={refreshAll} disabled={refreshing} className="btn-primary text-xs ml-auto disabled:opacity-50" data-testid="ua-trend-refresh-all">
          {refreshing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Refresh…</> : <><RefreshCw className="w-3.5 h-3.5" /> Rafraîchir tout</>}
        </button>
      </div>

      <div className="noam-card p-5">
        <div className="noam-overline mb-3">Sources actives</div>
        <div className="space-y-2">
          {sources.map((s) => (
            <div key={s.id} className="flex items-center gap-3 p-2.5 rounded-md bg-white/[0.02] border border-white/5">
              <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider ${s.level === "ultra" ? "bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30" : "bg-[#378ADD]/15 text-[#378ADD] border border-[#378ADD]/30"}`}>
                {s.level}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">{s.name}</div>
                <div className="text-[10px] text-zinc-500 font-mono truncate">{s.url}</div>
              </div>
              <span className={`text-[10px] px-2 py-0.5 rounded ${s.active ? "bg-[#1D9E75]/15 text-[#1D9E75]" : "bg-zinc-700/30 text-zinc-500"}`}>
                {s.active ? "ACTIVE" : "INACTIVE"}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="noam-card p-4 flex items-center gap-2 flex-wrap">
        <Filter className="w-3.5 h-3.5 text-zinc-500" />
        <select value={filter.level} onChange={(e) => setFilter({ ...filter, level: e.target.value })}
          className="text-xs bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white">
          <option value="">Tous niveaux</option>
          <option value="ultra">Ultra (privé)</option>
          <option value="platform">Plateforme</option>
        </select>
        <select value={filter.analyzed} onChange={(e) => setFilter({ ...filter, analyzed: e.target.value })}
          className="text-xs bg-black/30 border border-white/10 rounded-md px-2 py-1 text-white">
          <option value="">Tous</option>
          <option value="yes">Analysés</option>
          <option value="no">Non analysés</option>
        </select>
        <span className="text-[11px] text-zinc-500 ml-auto">{filtered.length} affiché(s)</span>
      </div>

      <div className="space-y-3" data-testid="ua-trend-signals">
        {filtered.map((sig) => (
          <UltraSignalCard
            key={sig.id} sig={sig} orgs={orgs}
            analyzing={analyzing === sig.id}
            pushing={pushing === sig.id}
            onAnalyze={() => analyze(sig)}
            onPush={(target_org_ids) => pushSignal(sig.id, target_org_ids)}
          />
        ))}
      </div>
    </div>
  );

  async function pushSignal(sig_id, org_ids) {
    setPushing(sig_id);
    try {
      const r = await api.post(`/ultra-admin/trend/signals/${sig_id}/push`, { org_ids });
      setSignals((arr) => arr.map((s) => (s.id === sig_id ? r.data : s)));
      notify.success(`Signal poussé vers ${org_ids.length} organisation(s).`);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setPushing(null); }
  }
}

function UltraSignalCard({ sig, orgs, analyzing, pushing, onAnalyze, onPush }) {
  const [showPush, setShowPush] = useState(false);
  const [selected, setSelected] = useState(new Set(sig.pushed_to || []));
  const fmt = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return iso; } };
  const crit = { critical: "#E24B4A", high: "#EF9F27", medium: "#378ADD", low: "#1D9E75" }[sig.criticality] || "#71717a";

  return (
    <div className="noam-card p-4" data-testid={`ua-trend-card-${sig.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap text-[11px]">
            <span className="text-zinc-400 font-medium">{sig.source_name}</span>
            <span className="text-zinc-700">·</span>
            <span className="text-zinc-500">{fmt(sig.published_at)}</span>
            {sig.criticality && (
              <span className="text-[10px] px-2 py-0.5 rounded uppercase tracking-wider" style={{ background: `${crit}1A`, color: crit, border: `1px solid ${crit}40` }}>
                {sig.criticality}
              </span>
            )}
            {sig.category && <span className="text-[10px] px-2 py-0.5 rounded bg-white/5 border border-white/10 text-zinc-300">{sig.category}</span>}
            {sig.is_constante_signal && (
              <span className="text-[10px] px-2 py-0.5 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/40 uppercase tracking-wider">
                Poussé · {sig.pushed_to?.length || 0} org
              </span>
            )}
          </div>
          <h3 className="text-sm text-white font-medium mt-1.5 leading-snug">{sig.title}</h3>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {sig.url && (
            <a href={sig.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded text-zinc-400 hover:text-white hover:bg-white/5">
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
          {!sig.analyzed && (
            <button onClick={onAnalyze} disabled={analyzing} className="btn-primary text-[11px] py-1.5 px-2.5 disabled:opacity-50" data-testid={`ua-trend-analyze-${sig.id}`}>
              {analyzing ? <><Loader2 className="w-3 h-3 animate-spin" /></> : <><Sparkles className="w-3 h-3" /> Analyser</>}
            </button>
          )}
          {sig.analyzed && (
            <button onClick={() => setShowPush((v) => !v)} className="btn-ghost text-[11px] py-1.5 px-2.5" data-testid={`ua-trend-push-toggle-${sig.id}`}>
              {sig.is_constante_signal ? "Modifier push" : "Pousser vers orgs"}
            </button>
          )}
        </div>
      </div>
      {showPush && (
        <div className="mt-3 pt-3 border-t border-white/5">
          <div className="text-[11px] text-zinc-400 mb-2">Sélectionnez les organisations à qui pousser ce signal :</div>
          <div className="grid grid-cols-2 gap-1 max-h-48 overflow-y-auto">
            {orgs.map((o) => (
              <label key={o.id} className="flex items-center gap-2 text-xs text-zinc-200 px-2 py-1 hover:bg-white/5 rounded cursor-pointer" data-testid={`ua-trend-push-org-${o.slug}`}>
                <input type="checkbox" checked={selected.has(o.id)} onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(o.id); else next.delete(o.id);
                  setSelected(next);
                }} />
                <span>{o.name}</span>
                <span className="text-[10px] text-zinc-500">({o.plan})</span>
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setShowPush(false)} className="btn-ghost text-xs">Annuler</button>
            <button onClick={() => { onPush(Array.from(selected)); setShowPush(false); }} disabled={pushing || selected.size === 0}
              className="btn-primary text-xs disabled:opacity-50" data-testid={`ua-trend-push-submit-${sig.id}`}>
              {pushing ? <Loader2 className="w-3 h-3 animate-spin" /> : <>Pousser vers {selected.size} org{selected.size > 1 ? "s" : ""}</>}
            </button>
          </div>
        </div>
      )}
      {sig.analyzed && (sig.impacts?.length || sig.opportunities?.length || sig.risques?.length) && (
        <div className="grid md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-white/5">
          {["impacts", "opportunities", "risques"].map((k) => {
            const items = sig[k] || [];
            if (items.length === 0) return null;
            const c = { impacts: "#378ADD", opportunities: "#1D9E75", risques: "#E24B4A" }[k];
            const lbl = { impacts: "Impacts", opportunities: "Opportunités", risques: "Risques" }[k];
            return (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: c }}>{lbl}</div>
                <ul className="space-y-0.5">
                  {items.slice(0, 3).map((it, i) => (
                    <li key={i} className="text-[11px] text-zinc-300 flex items-start gap-1.5 leading-relaxed">
                      <span className="w-1 h-1 rounded-full mt-1.5 shrink-0" style={{ background: c }} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function LivePresencePanel() {
  const [data, setData] = useState(null);
  const [windowMin, setWindowMin] = useState(60);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (w) => {
    try {
      const r = await api.get(`/ultra-admin/online-users?window_min=${w}`);
      setData(r.data);
      setError(null);
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(windowMin);
    const id = setInterval(() => load(windowMin), 30_000); // refresh toutes les 30s
    return () => clearInterval(id);
  }, [load, windowMin]);

  const impersonate = async (orgId) => {
    if (!orgId) return;
    if (!window.confirm("Entrer dans cette organisation ?")) return;
    try {
      await api.post(`/workspace/admin/organisations/${orgId}/impersonate`);
      window.location.href = "/app/executive";
    } catch (e) {
      alert(fmtErr(e?.response?.data?.detail));
    }
  };

  const statusMeta = {
    online: { color: "#1D9E75", label: "En ligne", pulse: true },
    recent: { color: "#EF9F27", label: "Récent", pulse: false },
    idle: { color: "#A1A1AA", label: "Inactif", pulse: false },
    unknown: { color: "#A1A1AA", label: "—", pulse: false },
  };

  const fmtAgo = (s) => {
    if (s < 0) return "—";
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    return `${Math.floor(s / 3600)}h${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}`;
  };

  return (
    <div className="noam-card p-5" data-testid="ua-live-presence-panel">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="flex items-center gap-1.5">
          <span className="relative inline-flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-[#1D9E75] opacity-75 animate-ping" />
            <span className="relative rounded-full w-2 h-2 bg-[#1D9E75]" />
          </span>
          <span className="noam-overline">Connectés en direct</span>
        </div>
        {data && (
          <div className="flex items-center gap-3 ml-2 text-[11px]">
            <span data-testid="ua-presence-count-online">
              <span className="text-[#1D9E75] font-semibold">{data.counts.online}</span> <span className="text-zinc-500">en ligne</span>
            </span>
            <span data-testid="ua-presence-count-recent">
              <span className="text-[#EF9F27] font-semibold">{data.counts.recent}</span> <span className="text-zinc-500">récent</span>
            </span>
            <span data-testid="ua-presence-count-idle">
              <span className="text-zinc-400 font-semibold">{data.counts.idle}</span> <span className="text-zinc-500">inactif</span>
            </span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={windowMin}
            onChange={(e) => setWindowMin(Number(e.target.value))}
            className="noam-input text-[11px] py-1"
            data-testid="ua-presence-window"
          >
            <option value={15}>15 min</option>
            <option value={60}>1 heure</option>
            <option value={240}>4 heures</option>
            <option value={1440}>24 heures</option>
          </select>
          <button
            onClick={() => load(windowMin)}
            className="text-zinc-400 hover:text-[#378ADD] p-1.5 rounded hover:bg-white/5"
            title="Rafraîchir"
            data-testid="ua-presence-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-[#FCA5A5] mb-3">{error}</div>}

      {data && data.users.length === 0 && (
        <div className="text-zinc-500 italic text-xs py-4 text-center" data-testid="ua-presence-empty">
          Personne de connecté dans cette fenêtre.
        </div>
      )}

      {data && data.users.length > 0 && (
        <div className="space-y-1 max-h-80 overflow-y-auto" data-testid="ua-presence-list">
          {data.users.map((u) => {
            const meta = statusMeta[u.status] || statusMeta.unknown;
            return (
              <div
                key={u.user_id}
                className="grid grid-cols-12 items-center gap-2 px-2 py-2 rounded hover:bg-white/[0.02] text-xs border-b border-white/[0.03] last:border-b-0"
                data-testid={`ua-presence-user-${u.user_id}`}
              >
                <div className="col-span-1 flex items-center justify-center">
                  <span className="relative inline-flex w-2 h-2">
                    {meta.pulse && (
                      <span className="absolute inset-0 rounded-full opacity-75 animate-ping" style={{ background: meta.color }} />
                    )}
                    <span className="relative rounded-full w-2 h-2" style={{ background: meta.color }} />
                  </span>
                </div>
                <div className="col-span-4 truncate">
                  <div className="text-zinc-100 truncate">
                    {u.name || u.email}
                    {u.is_super_admin && <span className="ml-1 text-[9px] text-[#EF9F27]">★ ULTRA</span>}
                  </div>
                  {u.name && <div className="text-[10px] text-zinc-500 truncate">{u.email}</div>}
                </div>
                <div className="col-span-3 text-[11px] text-zinc-300 truncate">
                  {u.active_org_name ? (
                    <span title={u.active_org_slug}>{u.active_org_name}</span>
                  ) : (
                    <span className="text-zinc-600 italic">—</span>
                  )}
                </div>
                <div className="col-span-2 text-[10px]" style={{ color: meta.color }}>
                  {meta.label} · il y a {fmtAgo(u.seconds_ago)}
                </div>
                <div className="col-span-2 flex items-center justify-end">
                  {u.active_org_id && (
                    <button
                      onClick={() => impersonate(u.active_org_id)}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[#378ADD]/15 border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/25"
                      title={`Entrer dans ${u.active_org_name}`}
                      data-testid={`ua-presence-impersonate-${u.user_id}`}
                    >
                      <UserCheck className="w-3 h-3" /> Entrer
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ActivityPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/ultra-admin/activity").then((r) => setData(r.data)).catch((e) => setError(fmtErr(e?.response?.data?.detail)));
  }, []);

  if (error) return <div className="text-xs text-[#FCA5A5] p-3 border border-[#E24B4A]/30 bg-[#E24B4A]/10 rounded-md">{error}</div>;
  if (!data) return <div className="text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;

  const { kpis, plan_distribution, module_usage, events } = data;
  const KpiCard = ({ label, value, sub, color = "#378ADD", testid }) => (
    <div className="noam-card p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</div>
      <div className="noam-display text-3xl font-bold mt-2" style={{ color }}>{value}</div>
      {sub && <div className="text-[11px] text-zinc-400 mt-1">{sub}</div>}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ua-activity-panel">
      <div className="flex items-center gap-2 text-[#EF9F27]">
        <ActivityIcon className="w-4 h-4" />
        <span className="noam-overline">Tableau de bord plateforme — instantané</span>
        <span className="ml-auto text-[10px] text-zinc-500">Généré : {new Date(data.generated_at).toLocaleString("fr-FR")}</span>
      </div>

      <LivePresencePanel />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard label="Organisations" value={kpis.total_orgs} sub={`+${kpis.new_orgs_24h} en 24h`} color="#378ADD" testid="ua-kpi-orgs" />
        <KpiCard label="Utilisateurs" value={kpis.total_users} sub={`+${kpis.new_users_24h} en 24h`} color="#7F77DD" testid="ua-kpi-users" />
        <KpiCard label="Actifs 24h" value={kpis.active_users_24h} sub="Connectés dans les 24 dernières heures" color="#1D9E75" testid="ua-kpi-active-24h" />
        <KpiCard label="Actifs 7j" value={kpis.active_users_7d} sub="Connectés cette semaine" color="#EF9F27" testid="ua-kpi-active-7d" />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="noam-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-4 h-4 text-[#7F77DD]" />
            <span className="noam-overline">Répartition par plan</span>
          </div>
          <div className="space-y-3">
            {plan_distribution.map((p) => {
              const meta = PLAN_LABELS[p.plan] || PLAN_LABELS.test;
              const max = Math.max(...plan_distribution.map((x) => x.orgs), 1);
              const pct = (p.orgs / max) * 100;
              return (
                <div key={p.plan} data-testid={`ua-plan-row-${p.plan}`}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-white">{meta.label}</span>
                    <span className="text-zinc-400"><span className="text-white font-semibold">{p.orgs}</span> orgs · <span className="text-[#1D9E75]">{p.active_users_24h}</span> actifs 24h</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: meta.color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="noam-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Layers className="w-4 h-4 text-[#378ADD]" />
            <span className="noam-overline">Adoption par module</span>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {module_usage.map((m) => (
              <div key={m.module} className="flex items-center gap-2 text-xs" data-testid={`ua-module-row-${m.module}`}>
                <span className="text-zinc-300 flex-1 truncate">{m.label}</span>
                <span className="text-[10px] text-zinc-500">{m.records} entrées</span>
                <div className="w-20 h-1 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-[#378ADD] rounded-full" style={{ width: `${m.adoption_rate}%` }} />
                </div>
                <span className="text-[10px] text-[#378ADD] w-9 text-right">{m.adoption_rate}%</span>
              </div>
            ))}
            {module_usage.length === 0 && <div className="text-zinc-500 italic text-xs">Aucune donnée.</div>}
          </div>
        </div>
      </div>

      <div className="noam-card p-5">
        <div className="flex items-center gap-2 mb-4">
          <ActivityIcon className="w-4 h-4 text-[#1D9E75]" />
          <span className="noam-overline">Flux d'événements (7 derniers jours)</span>
        </div>
        <div className="space-y-1 max-h-80 overflow-y-auto">
          {events.length === 0 && <div className="text-zinc-500 italic text-xs">Aucun événement récent.</div>}
          {events.map((e, i) => {
            const color = e.type === "org_created" ? "#7F77DD" : e.type === "user_created" ? "#378ADD" : "#1D9E75";
            return (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-white/[0.02] text-xs" data-testid={`ua-event-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />
                <span className="text-zinc-200 flex-1 truncate">{e.label}</span>
                {e.plan && <span className="text-[10px] text-zinc-500 px-1.5 py-0.5 rounded bg-white/5">{e.plan}</span>}
                <span className="text-[10px] text-zinc-500">{e.at ? new Date(e.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// P2 Vague 10 — LLM Quota Panel (observability + plan management)
// ============================================================================
function LlmQuotaPanel() {
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [u, p, a] = await Promise.all([
        api.get("/ultra-admin/llm-usage"),
        api.get("/ultra-admin/llm-plans"),
        api.get("/ultra-admin/llm-alerts"),
      ]);
      setUsage(u.data);
      setPlans(p.data.plans || {});
      setAlerts(a.data.items || []);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updatePlan = async (orgId, newPlan) => {
    setSavingId(orgId);
    try {
      await api.put(`/ultra-admin/organisations/${orgId}/llm-plan`, { llm_plan: newPlan });
      await load();
    } catch (e) {
      alert("Échec mise à jour plan : " + (e?.response?.data?.detail || e.message));
    } finally { setSavingId(null); }
  };

  const resolveAlert = async (alertId) => {
    try {
      await api.post(`/ultra-admin/llm-alerts/${alertId}/resolve`);
      await load();
    } catch (e) {
      alert("Erreur : " + e.message);
    }
  };

  if (loading) {
    return <div className="text-xs text-zinc-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>;
  }

  const items = usage?.items || [];
  const ym = usage?.year_month;

  return (
    <div className="space-y-6" data-testid="ua-llm-panel">
      <div>
        <div className="flex items-center gap-2 noam-overline text-[#EF9F27]">
          <TrendingUp className="w-3 h-3" /> Quotas Claude — mois en cours ({ym})
        </div>
        <h2 className="noam-display text-xl mt-1">Consommation IA par workspace</h2>
        <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
          Suivi temps réel des appels Claude pour chaque organisation. Les plans définissent le quota mensuel maximum.
          Tout dépassement déclenche une alerte ci-dessous.
        </p>
      </div>

      {alerts.length > 0 && (
        <div className="noam-card border-red-500/30 p-4 space-y-2" data-testid="ua-llm-alerts">
          <div className="text-xs uppercase tracking-wider text-red-400 font-semibold">
            {alerts.length} alerte{alerts.length > 1 ? "s" : ""} active{alerts.length > 1 ? "s" : ""}
          </div>
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center justify-between text-xs bg-red-500/5 p-2 rounded" data-testid={`ua-llm-alert-${a.id}`}>
              <div>
                <strong className="text-zinc-100">{a.organisation_id}</strong> — plan {a.plan}, {a.used}/{a.limit} appels,
                {a.hit_count} tentatives bloquées (dernière: {a.last_endpoint})
              </div>
              <button
                onClick={() => resolveAlert(a.id)}
                className="btn-ghost text-[10px]"
                data-testid={`ua-llm-alert-resolve-${a.id}`}
              >Résoudre</button>
            </div>
          ))}
        </div>
      )}

      <div className="noam-card overflow-x-auto" data-testid="ua-llm-table">
        <table className="w-full text-xs">
          <thead className="border-b border-white/10">
            <tr className="text-zinc-500 text-[10px] uppercase tracking-wider">
              <th className="text-left p-2">Organisation</th>
              <th className="text-left p-2 w-32">Plan</th>
              <th className="text-left p-2 w-24">Appels</th>
              <th className="text-left p-2 w-28">Quota</th>
              <th className="text-left p-2 w-40">Utilisation</th>
              <th className="text-left p-2">Top endpoints</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-zinc-500">Aucune consommation IA ce mois-ci.</td></tr>
            ) : items.map((it) => {
              const pct = it.percent || 0;
              const color = pct >= 100 ? "#EF4444" : pct >= 80 ? "#EF9F27" : "#1D9E75";
              const topEndpoints = Object.entries(it.by_endpoint || {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 4)
                .map(([k, v]) => `${k} (${v})`)
                .join(" · ");
              return (
                <tr key={it.organisation_id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="p-2">
                    <div className="text-zinc-100">{it.organisation_name}</div>
                    <div className="text-[10px] text-zinc-500">{it.organisation_slug}</div>
                  </td>
                  <td className="p-2">
                    <select
                      value={it.plan}
                      onChange={(e) => updatePlan(it.organisation_id, e.target.value)}
                      disabled={savingId === it.organisation_id}
                      className="noam-input text-xs"
                      data-testid={`ua-llm-plan-${it.organisation_id}`}
                    >
                      {Object.keys(plans).map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </td>
                  <td className="p-2 font-bold" style={{ color }}>{it.calls_count?.toLocaleString("fr-CH") || 0}</td>
                  <td className="p-2 text-zinc-400">{it.limit?.toLocaleString("fr-CH") || 0}</td>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-24 rounded-full bg-white/5 overflow-hidden">
                        <div className="h-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }} />
                      </div>
                      <span className="text-[10px]" style={{ color }}>{pct.toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className="p-2 text-[10px] text-zinc-400 truncate max-w-md" title={topEndpoints}>{topEndpoints || "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-zinc-500">
        Plans disponibles : {Object.entries(plans).map(([k, v]) => `${k} (${v.toLocaleString("fr-CH")}/mois)`).join(" · ")}
      </div>
    </div>
  );
}

// ============================================================================
// P2 Vague 11 — Observability Panel (Tavily, Claude errors, P95, crons)
// ============================================================================
function ObservabilityPanel() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [obs, alertsRes] = await Promise.all([
        api.get("/ultra-admin/observability"),
        api.get("/ultra-admin/doctrine-alerts").catch(() => ({ data: { items: [] } })),
      ]);
      setData(obs.data);
      setAlerts(alertsRes.data.items || []);
    } catch (e) {
      setError(fmtErr(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  const resolveAlert = async (id) => {
    try { await api.post(`/ultra-admin/doctrine-alerts/${id}/resolve`); await load(); }
    catch { /* silent */ }
  };

  if (loading && !data) {
    return <div className="text-xs text-zinc-500"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Chargement…</div>;
  }
  if (error) {
    return <div className="text-xs text-red-400">{error}</div>;
  }
  if (!data) return null;

  const tav = data.tavily_24h || {};
  const claude = data.claude_errors_24h || {};
  const slow = data.top_slow_routes || [];
  const crons = data.crons || [];

  const cronStatusColor = (c) => {
    if (c.last_status !== "ok") return "#EF4444";
    if (c.staleness_minutes != null && c.staleness_minutes > 1440) return "#EF9F27";
    return "#1D9E75";
  };

  return (
    <div className="space-y-6" data-testid="ua-obs-panel">
      <div>
        <div className="flex items-center gap-2 noam-overline text-[#378ADD]">
          <ActivityIcon className="w-3 h-3" /> Observabilité technique · 24h
        </div>
        <h2 className="noam-display text-xl mt-1">Santé de la plateforme</h2>
        <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
          Latence Tavily, erreurs Claude, P95 des routes API et état des crons. Rafraîchi automatiquement toutes les 30 secondes.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <ObsKPI label="Tavily — appels 24h" value={tav.calls || 0} color="#378ADD" testid="obs-kpi-tavily-calls" />
        <ObsKPI label="Latence moy. Tavily" value={`${tav.avg_latency_ms || 0} ms`} color="#1D9E75" testid="obs-kpi-tavily-avg" />
        <ObsKPI label="Tavily — erreurs" value={tav.errors || 0} color={tav.errors > 0 ? "#EF4444" : "#1D9E75"} testid="obs-kpi-tavily-errors" />
        <ObsKPI label="Erreurs Claude 24h" value={claude.total || 0} color={claude.total > 0 ? "#EF9F27" : "#1D9E75"} testid="obs-kpi-claude-errors" />
      </div>

      <SearchProvidersCard />

      {alerts.length > 0 && (
        <div className="noam-card p-4 border-red-500/30" data-testid="obs-doctrine-alerts">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
            <span className="noam-overline text-red-400">{alerts.length} alerte{alerts.length > 1 ? "s" : ""} doctrine — contradictions détectées</span>
          </div>
          <div className="space-y-2">
            {alerts.slice(0, 8).map((a) => (
              <div key={a.id} className="flex items-start justify-between gap-2 bg-red-500/5 border border-red-500/20 rounded p-2" data-testid={`obs-doctrine-alert-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs">
                    <strong className="text-zinc-100">{a.organisation_name}</strong>
                    <span className="text-zinc-500"> · {a.source_module || "?"}</span>
                    <span className="text-red-400 ml-1">score {a.score_conformite}/100</span>
                  </div>
                  <div className="text-[11px] text-zinc-300 mt-0.5 line-clamp-2">{a.synthese}</div>
                  {a.label && <div className="text-[10px] text-zinc-500 mt-0.5">{a.label}</div>}
                </div>
                <button
                  onClick={() => resolveAlert(a.id)}
                  className="btn-ghost text-[10px] flex-shrink-0"
                  data-testid={`obs-doctrine-resolve-${a.id}`}
                >Résoudre</button>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="noam-card p-4" data-testid="obs-crons">
        <div className="flex items-center gap-2 mb-3">
          <Loader2 className="w-3.5 h-3.5 text-zinc-400" />
          <span className="noam-overline">État des crons</span>
        </div>
        {crons.length === 0 ? (
          <div className="text-xs text-zinc-500">Aucun cron n'a encore tiqué (boot en cours).</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {crons.map((c) => (
              <div key={c.name} className="bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`obs-cron-${c.name}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs font-semibold text-zinc-100">{c.name}</div>
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: cronStatusColor(c) }} />
                </div>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Dernier tick : {c.staleness_minutes != null ? `il y a ${c.staleness_minutes} min` : "—"}
                </div>
                <div className="text-[10px] text-zinc-500">Status : <span className={c.last_status === "ok" ? "text-emerald-400" : "text-red-400"}>{c.last_status}</span></div>
                {c.last_error && <div className="text-[10px] text-red-400 mt-1 truncate" title={c.last_error}>{c.last_error}</div>}
                {c.extra && Object.entries(c.extra).length > 0 && (
                  <div className="text-[10px] text-zinc-400 mt-1">
                    {Object.entries(c.extra).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {claude.total > 0 && (
        <div className="noam-card p-4" data-testid="obs-claude">
          <div className="flex items-center gap-2 mb-3">
            <ShieldX className="w-3.5 h-3.5 text-[#EF9F27]" />
            <span className="noam-overline">Erreurs Claude 24h — par type</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(claude.by_kind).map(([k, v]) => (
              <div key={k} className="bg-white/[0.02] border border-white/5 rounded p-3">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
                <div className="text-xl font-bold text-[#EF9F27] mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="noam-card p-4" data-testid="obs-slow-routes">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-3.5 h-3.5 text-[#378ADD]" />
          <span className="noam-overline">Top 5 routes les plus lentes (max 24h)</span>
        </div>
        {slow.length === 0 ? (
          <div className="text-xs text-zinc-500">Aucune mesure encore disponible.</div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-zinc-500 text-[10px] uppercase tracking-wider border-b border-white/10">
                <th className="text-left p-2">Route</th>
                <th className="text-right p-2 w-20">Avg</th>
                <th className="text-right p-2 w-20">Max</th>
                <th className="text-right p-2 w-20">Count</th>
                <th className="text-right p-2 w-20">5xx</th>
              </tr>
            </thead>
            <tbody>
              {slow.map((r) => (
                <tr key={r.route} className="border-b border-white/5">
                  <td className="p-2 text-zinc-100 font-mono text-[11px]">{r.route}</td>
                  <td className="p-2 text-right text-zinc-400">{r.avg_ms} ms</td>
                  <td className="p-2 text-right" style={{ color: r.max_ms > 2000 ? "#EF9F27" : "#1D9E75" }}>{r.max_ms} ms</td>
                  <td className="p-2 text-right text-zinc-400">{r.count}</td>
                  <td className="p-2 text-right" style={{ color: r.errors_5xx > 0 ? "#EF4444" : "#1D9E75" }}>{r.errors_5xx}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] text-zinc-600">Snapshot : {data.snapshot_at} · DB : <span className={data.db_ok === false ? "text-red-400" : "text-emerald-400"}>{data.db_ok === false ? "DOWN" : "OK"}</span></div>
    </div>
  );
}

function ObsKPI({ label, value, color, testid }) {
  return (
    <div className="noam-card p-3" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

// =====================================================================
// V23.14.16 — Data Layer cartography (Ultra Admin)
// =====================================================================
function DataLayerPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("doc_count_desc");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/ultra-admin/data-layer");
      setData(r.data);
    } catch (e) {
      notify.error(fmtErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="noam-card p-4 text-zinc-400 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Chargement…</div>;
  if (!data) return null;

  const { summary, items } = data;

  const statusMeta = {
    in_sync: { color: "#1D9E75", label: "Synchronisé" },
    missing_in_db: { color: "#E24B4A", label: "Manquante en DB" },
    event_driven: { color: "#A1A1AA", label: "Event-driven" },
    orphan_in_db: { color: "#EF9F27", label: "Orpheline en DB" },
  };

  // Filtres + tri
  let rows = items;
  if (statusFilter !== "all") rows = rows.filter((r) => r.status === statusFilter);
  if (search.trim()) {
    const s = search.toLowerCase();
    rows = rows.filter((r) => r.name.toLowerCase().includes(s) || (r.refs || []).some((x) => x.toLowerCase().includes(s)));
  }
  rows = [...rows].sort((a, b) => {
    if (sortBy === "doc_count_desc") return b.doc_count - a.doc_count;
    if (sortBy === "ref_count_desc") return b.ref_count - a.ref_count;
    if (sortBy === "name_asc") return a.name.localeCompare(b.name);
    if (sortBy === "last_write_desc") return (b.last_write_at || "").localeCompare(a.last_write_at || "");
    return 0;
  });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <ObsKPI label="Référencées" value={summary.referenced ?? summary.total} color="#378ADD" testid="dl-card-total" />
        <ObsKPI label="In sync" value={summary.in_sync} color="#1D9E75" testid="dl-card-sync" />
        <ObsKPI label="Manquantes" value={summary.missing_in_db} color="#E24B4A" testid="dl-card-missing" />
        <ObsKPI label="Event-driven" value={summary.event_driven} color="#A1A1AA" testid="dl-card-event" />
        <ObsKPI label="Orphelines" value={summary.orphan_in_db} color="#EF9F27" testid="dl-card-orphan" />
      </div>

      <div className="noam-card p-3">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <input
            type="text"
            placeholder="Filtrer par nom ou fichier…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="noam-input text-xs flex-1 min-w-[200px]"
            data-testid="dl-search"
          />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="noam-input text-xs" data-testid="dl-status-filter">
            <option value="all">Tous statuts</option>
            <option value="in_sync">Synchronisées</option>
            <option value="missing_in_db">Manquantes en DB</option>
            <option value="event_driven">Event-driven</option>
            <option value="orphan_in_db">Orphelines DB</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="noam-input text-xs" data-testid="dl-sort">
            <option value="doc_count_desc">Tri : nb docs ↓</option>
            <option value="ref_count_desc">Tri : nb refs code ↓</option>
            <option value="last_write_desc">Tri : dernière écriture ↓</option>
            <option value="name_asc">Tri : nom A→Z</option>
          </select>
          <button onClick={load} className="btn-secondary text-xs" data-testid="dl-refresh">
            <RefreshCw className="w-3 h-3" /> Rafraîchir
          </button>
        </div>

        <div className="text-[11px] text-zinc-500 mb-2">{rows.length} collections affichées sur {items.length}</div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="dl-table">
            <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="text-left py-2 px-2">Collection</th>
                <th className="text-left py-2 px-2">Statut</th>
                <th className="text-right py-2 px-2">Docs</th>
                <th className="text-right py-2 px-2">Refs</th>
                <th className="text-left py-2 px-2">Dernière écriture</th>
                <th className="text-left py-2 px-2">Exemples de refs code</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.name} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`dl-row-${r.name}`}>
                  <td className="py-2 px-2 font-mono text-[11px] text-zinc-200">{r.name}</td>
                  <td className="py-2 px-2">
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider" style={{ color: statusMeta[r.status]?.color, background: `${statusMeta[r.status]?.color}1f` }}>
                      {statusMeta[r.status]?.label || r.status}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right font-mono">{r.doc_count}</td>
                  <td className="py-2 px-2 text-right font-mono">{r.ref_count}</td>
                  <td className="py-2 px-2 text-[10px] text-zinc-400">{r.last_write_at ? r.last_write_at.slice(0, 16).replace("T", " ") : "—"}</td>
                  <td className="py-2 px-2 text-[10px] text-zinc-500 font-mono">
                    {(r.refs || []).slice(0, 3).map((ref) => <div key={ref} className="truncate max-w-[280px]">{ref}</div>)}
                    {r.ref_count > 3 && <div className="text-zinc-600 italic">+ {r.ref_count - 3} autres</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Search Providers Card — Tavily → Brave → DuckDuckGo fallback chain
// ============================================================================
function SearchProvidersCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try { const r = await api.get("/ultra-admin/search-providers"); setStatus(r.data); }
    catch { /* silent */ }
  };

  useEffect(() => { load(); const t = setInterval(load, 30_000); return () => clearInterval(t); }, []);

  const reset = async (provider) => {
    setBusy(provider);
    try {
      const r = await api.post("/ultra-admin/search-providers/reset", { provider });
      if (r.data.was_disabled) notify.success(`Provider « ${provider} » réactivé.`);
      else notify.info(`Provider « ${provider} » était déjà actif.`);
      await load();
    } catch (e) {
      notify.error(fmtErr(e?.response?.data?.detail));
    } finally { setBusy(null); }
  };

  if (!status) return null;

  const providers = [
    { id: "tavily", name: "Tavily", role: "Primaire (1 000 req/mois gratuit)", color: "#378ADD" },
    { id: "brave", name: "Brave Search", role: "Fallback 1 (2 000 req/mois gratuit)", color: "#EF9F27" },
    { id: "ddg", name: "DuckDuckGo", role: "Fallback 2 (gratuit, illimité)", color: "#1D9E75" },
  ];

  const fmtCountdown = (s) => {
    if (!s || s <= 0) return null;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    if (m >= 60) return `${Math.floor(m/60)}h ${m%60}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  return (
    <div className="noam-card p-4" data-testid="ua-search-providers">
      <div className="flex items-center gap-2 mb-3">
        <ActivityIcon className="w-3.5 h-3.5 text-[#EF9F27]" />
        <span className="noam-overline">Recherche Web — chaîne de fallback</span>
      </div>
      <p className="text-[11px] text-zinc-400 mb-3 max-w-2xl">
        Quand un provider atteint son quota, le système bascule automatiquement vers le suivant.
        Un provider en quota est désactivé 1h puis réessayé. Reset manuel possible après recharge de quota.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {providers.map((p) => {
          const st = status[p.id] || {};
          const ok = !!st.available;
          const keyOk = status.keys_configured?.[p.id];
          const wait = fmtCountdown(st.seconds_until_retry);
          return (
            <div key={p.id} className="bg-white/[0.02] border border-white/5 rounded p-3" data-testid={`provider-${p.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: ok && keyOk ? p.color : "#52525b" }} />
                  <span className="text-sm font-semibold text-zinc-100">{p.name}</span>
                </div>
                {keyOk ? (
                  ok ? (
                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: "#1D9E75", background: "#1D9E751f" }}>Actif</span>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: "#EF4444", background: "#EF44441f" }}>En cooldown</span>
                  )
                ) : (
                  <span className="text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider" style={{ color: "#A1A1AA", background: "#3f3f461f" }}>Pas de clé</span>
                )}
              </div>
              <div className="text-[10px] text-zinc-500 mt-1">{p.role}</div>
              {!ok && wait && (
                <div className="text-[11px] text-amber-400 mt-2">Réessai automatique dans {wait}</div>
              )}
              {!ok && keyOk && (
                <button
                  onClick={() => reset(p.id)}
                  disabled={busy === p.id}
                  className="btn-ghost text-[10px] mt-2"
                  data-testid={`provider-reset-${p.id}`}
                >
                  {busy === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Réactiver maintenant
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// V23.15.38 — Doctrine 3Q Coverage — visible uniquement en Ultra Admin.
// Rappel : les 3Q sont un OUTIL DE LECTURE, pas un remplacement des 51 doctrines.
function Doctrine3QPanel() {
  return (
    <div className="space-y-4" data-testid="ua-doctrine3q-panel">
      <div className="noam-card p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#EF9F27]" />
          <div className="noam-overline text-[#EF9F27]">Ultra Admin · Dette doctrine</div>
        </div>
        <div className="noam-display text-lg font-semibold mt-1">Couverture Doctrine 3Q</div>
        <p className="text-xs text-zinc-400 mt-2 max-w-3xl leading-relaxed">
          Les 3 questions (« Et alors ? · Pourquoi maintenant ? · Que fais-je ? ») sont une <strong>lentille de lecture</strong> —
          elles ne remplacent pas les 51 doctrines qui font la richesse du raisonnement Noam. Elles projettent la sortie
          des doctrines dans un format lisible et actionnable. Ce tableau suit la progression de la lentille sur les modules.
        </p>
      </div>
      <ThreeQCoverageBadge />
    </div>
  );
}

// V23.15.44 — Ultra Admin · Aperçu des emails hebdo « Noam a pensé à vous »
// Contrôle éditorial : liste des users éligibles, statuts, preview HTML.
function EmailsHebdoPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewUserEmail, setPreviewUserEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/noam-thinking/admin/list");
      setData(r.data);
    } catch (e) {
      notify.error(fmtErr(e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openPreview = async (userId, email) => {
    try {
      const r = await api.get(`/workspace/noam-thinking/admin/preview/${userId}`);
      setPreviewHtml(r.data.html);
      setPreviewUserEmail(email);
    } catch (e) {
      notify.error(fmtErr(e?.response?.data?.detail || e.message));
    }
  };

  const STATUS_LABELS = {
    eligible: { label: "Éligible", color: "#1D9E75" },
    envoye_recemment: { label: "Envoyé récemment", color: "#7F77DD" },
    silencieux_rien_a_dire: { label: "Silencieux · rien à dire", color: "#A1A1AA" },
    cockpit_dormant: { label: "Modules dormants", color: "#71717A" },
    desabonne: { label: "Désabonné", color: "#EF9F27" },
    inactif: { label: "Inactif", color: "#71717A" },
    sans_org: { label: "Sans org", color: "#EF9F27" },
  };

  return (
    <div className="space-y-4" data-testid="ua-emails-hebdo-panel">
      <div className="noam-card p-5">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#7F77DD]" />
          <div className="noam-overline text-[#7F77DD]">Ultra Admin · Emails hebdo</div>
        </div>
        <div className="noam-display text-lg font-semibold mt-1">Aperçu « Noam a pensé à vous »</div>
        <p className="text-xs text-zinc-400 mt-2 max-w-3xl leading-relaxed">
          Contrôle éditorial des emails hebdomadaires envoyés à tes utilisateurs actifs (environnement consulté dans les 90 derniers jours).
          Un email n&apos;est envoyé QUE si des signaux pertinents existent — les fantômes ne sont jamais dérangés.
        </p>
        {data && !data.resend_configured && (
          <div className="mt-3 text-xs text-[#EF9F27] flex items-center gap-1.5 bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded px-2 py-1.5">
            <AlertTriangle className="w-3 h-3" />
            Resend n&apos;est pas configuré — aucun email n&apos;est effectivement envoyé. Ajoute <code className="mx-1 px-1 bg-white/10 rounded">RESEND_API_KEY</code> pour activer.
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-zinc-400 text-sm text-center py-6">Chargement…</div>
      ) : data && data.rows ? (
        <div className="noam-card overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="text-xs text-zinc-400">
              <strong className="text-white">{data.count}</strong> utilisateurs suivis
            </div>
            <button onClick={load} className="btn-ghost text-xs" data-testid="ua-emails-hebdo-refresh">
              <RefreshCw className="w-3 h-3" /> Rafraîchir
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.02] border-b border-white/5">
                <tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500">
                  <th className="px-3 py-2">Utilisateur</th>
                  <th className="px-3 py-2">Organisation</th>
                  <th className="px-3 py-2">Dernière connexion</th>
                  <th className="px-3 py-2">Dernier envoi</th>
                  <th className="px-3 py-2">Statut</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => {
                  const st = STATUS_LABELS[r.status] || { label: r.status, color: "#A1A1AA" };
                  return (
                    <tr key={r.user_id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`ua-emails-row-${r.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="text-zinc-100">{r.name || "—"}</div>
                        <div className="text-[10px] text-zinc-500">{r.email}</div>
                      </td>
                      <td className="px-3 py-2 text-zinc-300">{r.org_name || "—"}</td>
                      <td className="px-3 py-2 text-zinc-500 text-[10px] tabular-nums">
                        {r.last_login_at ? r.last_login_at.slice(0, 10) : "jamais"}
                      </td>
                      <td className="px-3 py-2 text-zinc-500 text-[10px] tabular-nums">
                        {r.last_sent_at ? r.last_sent_at.slice(0, 10) : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded font-semibold"
                          style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}55` }}
                        >
                          {st.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => openPreview(r.user_id, r.email)}
                          className="text-[10px] text-[#7F77DD] hover:underline"
                          data-testid={`ua-emails-preview-${r.user_id}`}
                        >
                          Prévisualiser
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-zinc-400 text-sm text-center py-6">Aucune donnée.</div>
      )}

      {previewHtml && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setPreviewHtml(null)}
          data-testid="ua-emails-preview-modal"
        >
          <div
            className="bg-zinc-950 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden border border-white/10 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-xs text-zinc-400">Aperçu email · <strong className="text-white">{previewUserEmail}</strong></div>
              <button onClick={() => setPreviewHtml(null)} className="text-zinc-400 hover:text-white" data-testid="ua-emails-preview-close">
                ✕
              </button>
            </div>
            <iframe
              srcDoc={previewHtml}
              title="Aperçu email"
              sandbox=""
              className="flex-1 w-full bg-white"
              style={{ minHeight: "500px" }}
            />
          </div>
        </div>
      )}
    </div>
  );
}




